import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import posthog from 'posthog-js';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// ─── EXTRACTED MODULES ────────────────────────────────────────────────────────
import {
  VAT_RATE, SUPABASE_URL, SUPABASE_KEY, SESSION_KEY, STAFF_FN, LOGO_URL,
  ROLE_TABS, CAN_EDIT, CAN_STATUS, CAN_SEE_TOTALS, ROLE_BADGE,
} from './lib/constants.js';
import T from './locales/translations.js';
import {
  CATEGORY_TAXONOMY, SHOPIFY_CAT_MAP,
  autoCategory, catLabel,
} from './data/categories.js';
import { auth, getValidToken, supaFetch, db } from './lib/supabase.js';
import { uid, fmt, fmtVat, catalogueUrl, thumbUrl } from './lib/utils.js';
import { readSession, isSuperAdminSession } from './lib/session.js';
import { openInvoiceSheet } from './lib/invoiceSheet.js';
import { downloadOrderWorkbook } from './lib/orderWorkbook.js';
import WhatsNew from './components/WhatsNew.jsx';
import { ViewAsCustomerLink } from './components/admin/ViewAsCustomerLink.jsx';
import { useIsMobile } from './hooks/useIsMobile.js';
import { useImageUpload } from './hooks/useImageUpload.js';
import Orders from './components/Orders.jsx';
import Applications from './components/Applications.jsx';
import Products from './components/Products.jsx';
import Customers from './components/Customers.jsx';
import Analytics from './components/Analytics.jsx';
import Vendors from './components/Vendors.jsx';
import VendorPortal from './components/VendorPortal.jsx';
import VendorApply from './components/VendorApply.jsx';
import RetailerRegister from './components/RetailerRegister.jsx';
import HeroBanner from './components/HeroBanner.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { CategoryIcon } from './lib/icons.js';
import { Search, ShoppingCart, Sparkles, Camera, Store, Home as HomeIcon } from 'lucide-react';
import { Icon } from './components/ui/Icon.jsx';
import { LangToggle } from './components/ui/LangToggle.jsx';
import { Badge } from './components/ui/Badge.jsx';
import { StatusChip } from './components/ui/StatusChip.jsx';
import { CategoryPill } from './components/ui/CategoryPill.jsx';
import { AdminSidebar } from './components/admin/AdminSidebar.jsx';
import { useProducts } from './hooks/useProducts.js';
import { useStockTracking, useOwnProductsFirst, useSaveSetting } from './hooks/useSettings.js';
import { encodeOrderDraft, decodeOrderDraft } from './lib/orderShare.js';
import SharedOrderModal from './components/SharedOrderModal.jsx';
import { useCustomers } from './hooks/useCustomers.js';
import { useAllOrders } from './hooks/useOrders.js';
import { useCart } from './catalogue/hooks/useCart.js';
import { useMyOrders, useOrderDetail } from './catalogue/hooks/useMyOrders.js';
import { useTokenProfile } from './hooks/useTokenProfile.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is "fresh" for 5 min — switching tabs won't trigger a hard refetch
      // if the cache is still within this window.
      staleTime: 5 * 60 * 1000,
      // Keep unused cache entries alive for 10 min after a component unmounts.
      // The admin almost always switches back to the same tab within that window,
      // so they get instant data instead of a loading spinner.
      gcTime: 10 * 60 * 1000,
    },
  },
});

// ─── LOADING SCREEN ───────────────────────────────────────────────────────────
// Homeware-marketplace brand moment: self-drawing line art of a curated shelf
// (vase, lamp, plates, cup — "everything for the home"), aurora glow backdrop,
// staggered reveal. Pure CSS/SVG — no dependencies.
function LoadingScreen({ lang = 'en' }) {
  const isAr = lang === 'ar';
  const CSS = [
    '@keyframes sdra-fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}',
    '@keyframes sdra-fadeIn{from{opacity:0}to{opacity:1}}',
    '@keyframes sdra-drift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(40px,-30px) scale(1.15)}}',
    '@keyframes sdra-drift2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-50px,25px) scale(1.1)}}',
    '@keyframes sdra-draw{0%{stroke-dashoffset:var(--len)}55%,70%{stroke-dashoffset:0;opacity:1}92%,100%{stroke-dashoffset:0;opacity:0}}',
    '@keyframes sdra-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}',
    '@keyframes sdra-bar{0%{background-position:-280px 0}100%{background-position:280px 0}}',
    '.sdra-stroke{fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:var(--len);stroke-dashoffset:var(--len);animation:sdra-draw 3.6s cubic-bezier(0.4,0,0.2,1) infinite}',
  ].join('');
  // Each shape declares its own path length so the draw animation is exact.
  const S = ({ d, len, delay, w = 1.6, o = 0.85 }) => (
    <path d={d} className='sdra-stroke' stroke={`rgba(235,241,226,${o})`} strokeWidth={w}
      style={{ '--len': len, animationDelay: `${delay}s` }} />
  );
  return (
    <div style={{
      minHeight:'100vh', display:'flex', flexDirection:'column',
      alignItems:'center', justifyContent:'center',
      background:'linear-gradient(160deg,#1f271c 0%,#2c3527 40%,#3A4534 72%,#4c5845 100%)',
      fontFamily:'Tajawal,sans-serif', overflow:'hidden', position:'relative',
    }}>
      <style>{CSS}</style>
      {/* Aurora glow blobs */}
      <div style={{ position:'absolute', width:420, height:420, borderRadius:'50%', filter:'blur(70px)',
        background:'radial-gradient(circle,rgba(139,160,110,0.22) 0%,transparent 65%)',
        top:'18%', left:'28%', animation:'sdra-drift1 9s ease-in-out infinite', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', width:360, height:360, borderRadius:'50%', filter:'blur(80px)',
        background:'radial-gradient(circle,rgba(214,197,161,0.13) 0%,transparent 65%)',
        bottom:'12%', right:'24%', animation:'sdra-drift2 11s ease-in-out infinite', pointerEvents:'none' }}/>

      <div style={{ animation:'sdra-fadeUp 0.85s cubic-bezier(0.22,1,0.36,1) forwards', marginBottom:34, opacity:0 }}>
        <img
          src={LOGO_URL}
          alt='Sedra Home'
          style={{ width:84, height:'auto', borderRadius:12, filter:'drop-shadow(0 6px 22px rgba(0,0,0,0.4))' }}
        />
      </div>

      {/* Curated homeware shelf — draws itself, holds, fades, redraws */}
      <div style={{ animation:'sdra-float 4.2s ease-in-out 0.4s infinite', width:190, height:120 }}>
        <svg viewBox='0 0 190 120' xmlns='http://www.w3.org/2000/svg' style={{ width:'100%', height:'100%', overflow:'visible' }}>
          {/* shelf line */}
          <S d='M8 104 H182' len={174} delay={0} w={2} o={0.55} />
          {/* vase with stems */}
          <S d='M34 104 C26 96 26 84 32 78 C36 74 36 68 33 64 H49 C46 68 46 74 50 78 C56 84 56 96 48 104 Z' len={150} delay={0.25} />
          <S d='M41 64 C41 54 38 50 33 46' len={26} delay={0.7} w={1.3} o={0.6} />
          <S d='M41 64 C41 52 45 48 50 44' len={28} delay={0.8} w={1.3} o={0.6} />
          {/* stacked plates */}
          <S d='M72 104 H108 M74 98 H106 M76 92 H104' len={100} delay={0.5} w={2.2} o={0.75} />
          <S d='M78 86 C78 80 102 80 102 86' len={40} delay={0.85} w={1.6} o={0.6} />
          {/* teacup */}
          <S d='M124 90 H146 C146 100 142 104 135 104 C128 104 124 100 124 90 Z' len={70} delay={0.65} />
          <S d='M146 92 C152 92 152 100 145 100' len={22} delay={1.0} w={1.4} o={0.65} />
          {/* table lamp */}
          <S d='M160 64 H182 L176 80 H166 Z M171 80 V98 M163 104 H179 M163 104 C163 100 166 98 171 98 C176 98 179 100 179 104' len={130} delay={0.45} />
          {/* hanging pendant light */}
          <S d='M95 6 V22 M87 34 C87 26 103 26 103 34 L99 40 H91 Z' len={70} delay={1.1} w={1.4} o={0.6} />
        </svg>
      </div>

      <div style={{
        animation:'sdra-fadeUp 0.75s cubic-bezier(0.22,1,0.36,1) 0.4s both',
        color:'rgba(255,255,255,0.72)', fontSize:11, fontWeight:300,
        letterSpacing: isAr ? 'normal' : '0.32em', textTransform: isAr ? 'none' : 'uppercase',
        marginTop:30, marginBottom:8,
      }}>
        {isAr ? 'سدرة هوم' : 'SEDRA HOME'}
      </div>
      <div style={{
        animation:'sdra-fadeIn 0.6s ease 0.8s both',
        color:'rgba(205,220,185,0.55)', fontSize:9.5, fontWeight:400,
        letterSpacing: isAr ? 'normal' : '0.18em', textTransform: isAr ? 'none' : 'uppercase',
        marginBottom:24,
      }}>
        {isAr ? 'كل ما يخص المنزل — جملة' : 'Everything Home · Wholesale'}
      </div>
      <div style={{
        width:116, height:1.5, background:'rgba(255,255,255,0.10)',
        borderRadius:2, overflow:'hidden', animation:'sdra-fadeIn 0.5s ease 0.95s both',
      }}>
        <div style={{
          height:'100%',
          background:'linear-gradient(90deg,transparent 0%,rgba(205,220,185,0.9) 50%,transparent 100%)',
          backgroundSize:'280px 100%',
          animation:'sdra-bar 1.6s ease-in-out infinite',
        }}/>
      </div>
    </div>
  );
}
// ─── SKELETON LOADER ─────────────────────────────────────────────────────────
const SDRA_SHIMMER_CSS = '@keyframes sdra-shimmer{0%{background-position:-600px 0}100%{background-position:600px 0}}.sdra-skel{background:linear-gradient(90deg,#f0ede8 25%,#e8e4de 50%,#f0ede8 75%);background-size:600px 100%;animation:sdra-shimmer 1.4s infinite linear;border-radius:6px}';
function SkeletonAdminCard() {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '12px 14px', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', display: 'flex', gap: 12, alignItems: 'center' }}>
      <style>{SDRA_SHIMMER_CSS}</style>
      <div className="sdra-skel" style={{ width: 52, height: 52, borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div className="sdra-skel" style={{ width: '70%', height: 14 }} />
        <div className="sdra-skel" style={{ width: '40%', height: 11 }} />
        <div className="sdra-skel" style={{ width: '25%', height: 11 }} />
      </div>
      <div className="sdra-skel" style={{ width: 60, height: 26, borderRadius: 7, flexShrink: 0 }} />
    </div>
  );
}
function SkeletonAdminRow() {
  return (
    <tr style={{ borderTop: '1px solid #f0ebe3' }}>
      <style>{SDRA_SHIMMER_CSS}</style>
      {[14, 40, 160, 70, 60, 30, 64].map((w, i) => (
        <td key={i} style={{ padding: '10px 14px' }}>
          <div className="sdra-skel" style={{ width: w, height: i === 2 ? 40 : (i === 0 || i === 6 ? 14 : 12), borderRadius: i === 2 ? 8 : 6 }} />
        </td>
      ))}
    </tr>
  );
}
// Products and Customers are now in src/components/ and use TanStack Query hooks.

// ─── SETTINGS ─────────────────────────────────────────────────────────────────
function Settings({ lang, setLang }) {
  const t = T[lang];
  const ar = lang === 'ar';
  const [webhook, setWebhook] = useState('');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyWaPhone, setNotifyWaPhone] = useState('');
  const [companyName, setCompanyName] = useState('Sedra Home');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [ogImage, setOgImage] = useState('');
  const stockTracking = useStockTracking();
  const ownFirst = useOwnProductsFirst();
  const saveSetting = useSaveSetting();
  const [msg, setMsg] = useState('');
  const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 7, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  const btnStyle = (c = 'var(--sedra-olive)') => ({ padding: '10px 20px', borderRadius: 7, background: c, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600 });

  useEffect(() => {
    db.getSetting('webhook_url').then(v => setWebhook(v || ''));
    db.getSetting('notify_email').then(v => setNotifyEmail(v || ''));
    db.getSetting('notify_wa_phone').then(v => setNotifyWaPhone(v || ''));
    db.getSetting('company_name').then(v => setCompanyName(v || 'Sedra Home'));
    db.getSetting('company_address').then(v => setCompanyAddress(v || ''));
    db.getSetting('company_phone').then(v => setCompanyPhone(v || ''));
    db.getSetting('company_email').then(v => setCompanyEmail(v || ''));
    db.getSetting('og_image').then(v => setOgImage(v || ''));
  }, []);

  const save = async () => {
    try {
      await db.setSetting('webhook_url', webhook);
      setMsg(t.settingsSaved); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Error: ' + e.message); }
  };

  const test = async () => {
    if (!webhook) return;
    try {
      await fetch(webhook, { method: 'POST', body: JSON.stringify({ test: true, message: 'Sedra Wholesale test ping', timestamp: new Date().toISOString() }) });
      setMsg('Test sent!'); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Test failed — check the URL'); setTimeout(() => setMsg(''), 3000); }
  };

  const saveCompany = async () => {
    try {
      await db.setSetting('company_name', companyName);
      await db.setSetting('company_address', companyAddress);
      await db.setSetting('company_phone', companyPhone);
      await db.setSetting('company_email', companyEmail);
      setMsg(t.settingsSaved); setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg('Error: ' + e.message); }
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 24px', color: 'var(--sedra-forest-soft)' }}>{t.settings}</h2>
      {msg && <div style={{ padding: '10px 16px', background: '#c6f6d5', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{msg}</div>}

      {/* Company Details */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Building2" size={16} /> {ar ? 'بيانات الشركة' : 'Company Details'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 16px' }}>{ar ? 'تظهر في الفواتير المصدَّرة' : 'Shown on exported invoices'}</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          {[[ar?'اسم الشركة':'Company Name','text',companyName,setCompanyName,'Sedra Home'],
            [ar?'عنوان الشركة':'Address','text',companyAddress,setCompanyAddress,ar?'العنوان...':'Address…'],
            [ar?'هاتف الشركة':'Phone','text',companyPhone,setCompanyPhone,'+20...'],
            [ar?'البريد الإلكتروني':'Email','email',companyEmail,setCompanyEmail,'info@sedrahome.com'],
          ].map(([label, type, val, setter, ph]) => (
            <div key={label}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}</label>
              <input type={type} style={inputStyle} value={val} onChange={e => setter(e.target.value)} placeholder={ph} />
            </div>
          ))}
        </div>
        <button onClick={saveCompany} style={btnStyle()}>{t.saveSettings}</button>
      </div>

      {/* Social share image (WhatsApp / link-preview thumbnail) */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Image" size={16} /> {ar ? 'صورة المشاركة (واتساب / معاينة الرابط)' : 'Share Thumbnail (WhatsApp / link preview)'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 12px' }}>{ar ? 'تظهر عند مشاركة رابط الموقع. المقاس المثالي 1200×630. استخدم صورة مرفوعة على تخزين الموقع (Supabase) — روابط المواقع الخارجية تُتجاهل. اتركه فارغًا لاستخدام الصورة الافتراضية.' : 'Shown when the site link is shared. Ideal size 1200×630. Use an image hosted on site storage (Supabase) — external hosts are ignored. Leave blank to use the built-in default.'}</p>
        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{ar ? 'رابط الصورة' : 'Image URL'}</label>
        <input style={{ ...inputStyle, marginBottom: 12 }} value={ogImage} onChange={e => setOgImage(e.target.value)} placeholder="https://.../share.jpg" />
        {ogImage ? <img src={ogImage} alt="" style={{ maxWidth: 320, width: '100%', borderRadius: 8, border: '1px solid var(--sedra-line)', marginBottom: 12, display: 'block' }} /> : null}
        <button onClick={async () => { try { await db.setSetting('og_image', ogImage.trim()); setMsg(t.settingsSaved); setTimeout(() => setMsg(''), 3000); } catch (e) { setMsg('Error: ' + e.message); } }} style={btnStyle()}>{t.saveSettings}</button>
      </div>

      {/* Stock tracking toggle */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>{ar ? 'تتبع المخزون' : 'Stock Tracking'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 14px' }}>{ar ? 'عند الإيقاف، تُخفى حالات المخزون و"غير متوفر" من المتجر.' : 'When off, stock states and “Out of stock” are hidden from the storefront.'}</p>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          <input type="checkbox" checked={stockTracking} onChange={async e => { const v = e.target.checked; try { await saveSetting('stock_tracking_enabled', v ? 'true' : 'false'); setMsg(t.settingsSaved); setTimeout(() => setMsg(''), 3000); } catch (err) { setMsg('Error: ' + err.message); } }} style={{ width: 18, height: 18 }} />
          {stockTracking ? (ar ? 'مُفعّل' : 'Enabled') : (ar ? 'مُعطّل' : 'Disabled')}
        </label>
      </div>

      {/* Own products first (catalogue merchandising) */}
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>{ar ? 'منتجاتي أولاً' : 'My Products First'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 14px' }}>{ar ? 'عند التفعيل، تظهر منتجات سدرة هوم قبل منتجات البائعين في الكتالوج (يبقى الترتيب حسب الأحدث داخل كل مجموعة).' : 'When on, Sedra Home products appear before vendor products in the catalogue (newest/oldest still orders within each group).'}</p>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          <input type="checkbox" checked={ownFirst} onChange={async e => { const v = e.target.checked; try { await saveSetting('catalogue_own_first', v ? 'true' : 'false'); setMsg(t.settingsSaved); setTimeout(() => setMsg(''), 3000); } catch (err) { setMsg('Error: ' + err.message); } }} style={{ width: 18, height: 18 }} />
          {ownFirst ? (ar ? 'مُفعّل' : 'Enabled') : (ar ? 'مُعطّل' : 'Disabled')}
        </label>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>{t.language}</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <LangToggle lang={lang} setLang={setLang} variant="light" />
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Google Sheets Webhook</h3>
        <p style={{ fontSize: 13, color: '#888', margin: '0 0 16px' }}>
          Orders will be automatically sent to your Google Sheet when placed.
        </p>
        <div style={{ background: 'var(--sedra-cream)', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 13 }}>
          <strong>Setup steps:</strong><br />
          1. Go to <a href="https://sheets.new" target="_blank" rel="noreferrer">sheets.new</a> and create a sheet<br />
          2. Go to Extensions → Apps Script<br />
          3. Paste this code and save:<br />
          <code style={{ display: 'block', background: '#fff', padding: 10, borderRadius: 6, margin: '8px 0', fontSize: 12, wordBreak: 'break-all' }}>
            {`function doPost(e){var s=SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();var d=JSON.parse(e.postData.contents);s.appendRow([d.order_id,d.date,d.business_name,d.contact_name,d.phone,d.whatsapp,d.email,d.catalogue_type,d.items_summary,d.total,d.notes]);return ContentService.createTextOutput("OK");}`}
          </code>
          4. Click Deploy → New Deployment → Web App → Anyone → Deploy<br />
          5. Copy the URL and paste below
        </div>
        <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{t.webhookUrl}</label>
        <input style={{ ...inputStyle, marginBottom: 12 }} value={webhook} onChange={e => setWebhook(e.target.value)} placeholder="https://script.google.com/macros/s/..." />
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} style={btnStyle()}>{t.saveSettings}</button>
          <button onClick={test} style={btnStyle('#888')}>{t.testWebhook}</button>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Mail" size={16} /> Email Notifications</h3>
        <p style={{ fontSize: 13, color: "#888", margin: "0 0 12px" }}>Receive an email for every new order. First email requires a one-time confirmation click from Formsubmit.</p>
        <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>Notification Email</label>
        <input style={{ ...inputStyle, marginBottom: 12 }} value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} placeholder="you@example.com" type="email" />
        <button onClick={async () => { await db.setSetting("notify_email", notifyEmail); setMsg(t.settingsSaved); setTimeout(() => setMsg(""), 3000); }} style={btnStyle()}>{t.saveSettings}</button>
      </div>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--sedra-forest-soft)', display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Link" size={16} /> {lang === 'ar' ? 'روابط الكتالوجات' : 'Catalogue Links'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 16px' }}>{lang === 'ar' ? 'روابطك السرية للعملاء' : 'Your secret per-client links'}</p>
        <TokenManager lang={lang} />
      </div>
      <div style={{ background: "#fff", borderRadius: 10, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.07)", marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="MessageCircle" size={16} /> WhatsApp Notifications</h3>
        <div style={{ background: "var(--sedra-cream)", borderRadius: 8, padding: 14, marginBottom: 14, fontSize: 13, lineHeight: 1.7 }}>
          {lang === 'ar'
            ? 'الرقم الذي تصل إليه تنبيهات الطلبات الجديدة عبر واتساب. تُرسل الرسائل عبر Wati.'
            : 'The number that receives new-order alerts on WhatsApp. Messages are sent through Wati.'}
        </div>
        <label style={{ fontSize: 12, color: "#666", display: "block", marginBottom: 4 }}>WhatsApp number (with country code)</label>
        <input style={{ ...inputStyle, marginBottom: 10 }} value={notifyWaPhone} onChange={e => setNotifyWaPhone(e.target.value)} placeholder="+201234567890" />
        <button onClick={async () => { await db.setSetting("notify_wa_phone", notifyWaPhone); setMsg(t.settingsSaved); setTimeout(() => setMsg(""), 3000); }} style={btnStyle()}>{t.saveSettings}</button>
      </div>

    <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginTop: 20 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: 'var(--sedra-forest-soft)', display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="MessageCircle" size={16} /> {ar ? 'وسائل التواصل' : 'Communication Channels'}</h3>
      <p style={{ fontSize: 12, color: 'var(--sedra-ink-muted)', margin: '0 0 16px' }}>{ar ? 'وسائل التواصل مع عملائك' : 'Reach your customers on every platform'}</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12 }}>
        {[
          { icon: 'MessageCircle', label: 'WhatsApp Business', color: '#25D366', bg: '#f0fdf4', href: 'https://api.whatsapp.com/send?phone=201204114211' },
          { icon: 'Camera',        label: 'Instagram',         color: '#e1306c', bg: '#fdf0f5', href: 'https://www.instagram.com/sedrahome' },
          { icon: 'Monitor',       label: 'Website',           color: 'var(--status-info)', bg: 'var(--status-info-bg)', href: 'https://sedrahome.com' },
          { icon: 'Mail',          label: 'Email',             color: '#7c3aed', bg: '#f5f3ff', href: 'mailto:sales@sedrahome.com' },
          { icon: 'Phone',         label: 'Phone',             color: '#0891b2', bg: '#ecfeff', href: 'tel: +201204114211' },
          { icon: 'ClipboardList', label: ar ? 'روابط الكتالوج' : 'Catalogue Links', color: 'var(--sedra-sage-ink)', bg: '#f5f7f2', href: null },
        ].map(({ icon, label, color, bg, href }) => (
          <div key={label} style={{ background: bg, borderRadius: 10, padding: '14px', border: '1px solid', borderColor: color + '33', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon name={icon} size={22} color={color} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sedra-forest-soft)' }}>{label}</div>
              {href ? (
                <a href={href} target='_blank' rel='noreferrer'
                  style={{ fontSize: 11, color, textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  {ar ? 'فتح' : 'Open'} <Icon name="ExternalLink" size={11} />
                </a>
              ) : (
                <span style={{ fontSize: 11, color, fontWeight: 600 }}>{ar ? 'متوفر أعلاه' : 'Available above'}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
    </div>
  );
}

// ─── ADMIN SHELL ─────────────────────────────────────────────────────────────

// ─── DASHBOARD ──────────────────────────────────────────────────────────────────────────────
function Dashboard({ lang, setTab, canSeeTotals }) {
  const t = T[lang];
  // Self-fetching via TanStack Query — no more prop-drilling from AdminShell
  const { data: products  = [] } = useProducts();
  const { data: customers = [] } = useCustomers();
  const { data: orders    = [] } = useAllOrders();
  // Shared cached flag — fails closed (false) while loading / on error, so the
  // low-stock alert can't flash on when tracking is off (the default).
  const stockTracking = useStockTracking();

  const newOrders    = orders.filter(o => !o.status || o.status === 'new' || o.status === 'seen');
  const totalRev     = orders.reduce((s, o) => s + (Number(o.total) || 0), 0);
  const recent       = [...orders].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 6);

  // ── 30-day orders bar chart ──────────────────────────────────────────────────
  const chartDays = 30;
  const today = new Date(); today.setHours(23,59,59,999);
  const dayLabels = Array.from({ length: chartDays }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() - (chartDays - 1 - i));
    return d.toISOString().slice(0, 10);
  });
  const dayCounts = dayLabels.map(day => orders.filter(o => (o.created_at || '').slice(0, 10) === day).length);
  const maxCount  = Math.max(1, ...dayCounts);
  const chartH    = 72;
  const chartW    = 100; // percent-based via SVG viewBox
  const card = (label, value, color = 'var(--sedra-forest-soft)', onClick = null) => (
    <div
      onClick={onClick}
      style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', minWidth: 140, flex: 1, cursor: onClick ? 'pointer' : 'default', transition: 'transform 0.12s, box-shadow 0.12s' }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 18px rgba(0,0,0,0.13)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow='0 2px 8px rgba(0,0,0,0.07)'; }}
    >
      <div style={{ fontSize: 13, color: '#999', fontWeight: 600, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
  const sc = s => s === 'new' ? 'var(--status-info)' : s === 'confirmed' ? 'var(--status-success)' : s === 'cancelled' ? 'var(--status-danger)' : '#888';
  const ar = lang === 'ar';
  const [isLowStockModalOpen, setIsLowStockModalOpen] = useState(false);
  return (
    <div>
      <h2 style={{ margin: '0 0 24px', color: 'var(--sedra-forest-soft)', fontWeight: 800 }}>{t.dashboard}</h2>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        {card(ar ? '\u0637\u0644\u0628\u0627\u062a \u062c\u062f\u064a\u062f\u0629' : 'New Orders',    newOrders.length, 'var(--status-info)', setTab ? () => setTab('orders') : null)}
        {card(ar ? '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0637\u0644\u0628\u0627\u062a' : 'Total Orders', orders.length,  'var(--sedra-forest-soft)', setTab ? () => setTab('orders') : null)}
        {canSeeTotals && card(ar ? '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0625\u064a\u0631\u0627\u062f\u0627\u062a' : 'Revenue',      fmt(totalRev), 'var(--status-success)', null)}
        {card(ar ? '\u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a' : 'Products',     products.length, 'var(--sedra-forest-soft)', setTab ? () => setTab('products') : null)}
        {card(ar ? '\u0627\u0644\u0639\u0645\u0644\u0627\u0621' : 'Customers',    customers.length, 'var(--sedra-forest-soft)', setTab ? () => setTab('customers') : null)}
      </div>
      {/* ── 30-day chart ── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--sedra-forest-soft)' }}>{ar ? 'الطلبات — آخر 30 يوم' : 'Orders — Last 30 Days'}</h3>
          <span style={{ fontSize: 12, color: '#aaa' }}>{orders.filter(o => (o.created_at||'') >= dayLabels[0]).length} {ar ? 'طلب' : 'orders'}</span>
        </div>
        <svg viewBox={`0 0 ${chartDays * 12} ${chartH + 16}`} style={{ width: '100%', height: 110, display: 'block' }} preserveAspectRatio='xMidYMax meet'>
          {/* baseline */}
          <line x1={0} y1={chartH} x2={chartDays * 12} y2={chartH} stroke='#e8e3db' strokeWidth={0.5} />
          {dayCounts.map((cnt, i) => {
            const barH = cnt === 0 ? 1.5 : Math.max(5, (cnt / maxCount) * chartH);
            const x = i * 12 + 2;
            const isToday = i === chartDays - 1;
            return (
              <g key={i}>
                <rect x={x} y={chartH - barH} width={9} height={barH} rx={2}
                  fill={isToday ? 'var(--sedra-sage-ink)' : cnt > 0 ? '#b8cba8' : '#f0ede8'} />
                {cnt > 0 && (
                  <text x={x + 4.5} y={chartH - barH - 3} textAnchor='middle' fontSize={7} fill='var(--sedra-forest-soft)' fontWeight='700'>{cnt}</text>
                )}
              </g>
            );
          })}
          {/* X-axis labels — show ~5 evenly spaced dates */}
          {[0, 6, 13, 20, 29].map(i => (
            <text key={i} x={i * 12 + 6} y={chartH + 13} textAnchor='middle' fontSize={6.5} fill='#bbb'>
              {dayLabels[i]?.slice(5)}
            </text>
          ))}
        </svg>
      </div>

      {/* ── Low stock alert ── */}
      {(() => {
        if (!stockTracking) return null;
        const lowStock = products.filter(p => p.stock !== undefined && p.stock !== null && Number(p.stock) <= (Number(p.min_qty) || 1) * 2 && Number(p.stock) >= 0);
        if (lowStock.length === 0) return null;
        return (
          <>
            {/* Clickable summary card */}
            <div
              onClick={() => setIsLowStockModalOpen(true)}
              style={{ background: '#fff', borderRadius: 12, padding: '16px 24px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginBottom: 20, borderLeft: '4px solid #f59e0b', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.13)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name="TriangleAlert" size={16} color="var(--status-warn-dark)" />
                  <h3 style={{ margin: 0, fontSize: 15, color: 'var(--status-warn-dark)', fontWeight: 800 }}>{ar ? 'تحذير: مخزون منخفض' : 'Low Stock Warning'} ({lowStock.length})</h3>
                </div>
                <span style={{ fontSize: 12, color: 'var(--status-warn)', fontWeight: 600, flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3 }}>{ar ? 'عرض الكل' : 'View all'} <Icon name="ExternalLink" size={12} /></span>
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {lowStock.slice(0, 5).map(p => (
                  <StatusChip key={p.id} state={Number(p.stock) === 0 ? 'out' : 'low'} label={`${p.name_en} (${p.stock})`} style={{ fontSize: 11 }} />
                ))}
                {lowStock.length > 5 && (
                  <span style={{ fontSize: 11, color: 'var(--status-warn)', fontWeight: 700, padding: '3px 8px', alignSelf: 'center' }}>
                    +{lowStock.length - 5} {ar ? 'أخرى — انقر للعرض' : 'more — click to view'}
                  </span>
                )}
              </div>
            </div>

            {/* Low stock modal */}
            {isLowStockModalOpen && (
              <div
                onClick={e => { if (e.target === e.currentTarget) setIsLowStockModalOpen(false); }}
                style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
              >
                <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 660, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
                  {/* Modal header */}
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--sedra-line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Icon name="TriangleAlert" size={18} color="var(--status-warn-dark)" />
                      <h3 style={{ margin: 0, fontSize: 16, color: 'var(--status-warn-dark)', fontWeight: 800 }}>
                        {ar ? 'تحذير: مخزون منخفض' : 'Low Stock Warning'} — {lowStock.length} {ar ? 'منتج' : 'products'}
                      </h3>
                    </div>
                    <button
                      onClick={() => setIsLowStockModalOpen(false)}
                      style={{ background: 'var(--sedra-line)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', color: 'var(--sedra-ink-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    ><Icon name="X" size={16} /></button>
                  </div>
                  {/* Scrollable table */}
                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0, background: '#fafaf8', zIndex: 1 }}>
                        <tr style={{ borderBottom: '2px solid var(--sedra-line)' }}>
                          {[ar ? 'الكود' : 'SKU', ar ? 'الاسم (EN)' : 'Name (EN)', ar ? 'الاسم (AR)' : 'Name (AR)', ar ? 'المخزون' : 'Stock', ar ? 'الحد الأدنى' : 'Min Qty'].map((h, i) => (
                            <th key={i} style={{ padding: '10px 14px', textAlign: i === 3 || i === 4 ? 'center' : (ar ? 'right' : 'left'), color: '#888', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {lowStock.map(p => {
                          const isOut = Number(p.stock) === 0;
                          return (
                            <tr key={p.id} style={{ borderBottom: '1px solid var(--sedra-line)' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#fffbf0'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}
                            >
                              <td style={{ padding: '10px 14px' }}>
                                <span style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--sedra-line)', padding: '2px 8px', borderRadius: 5, userSelect: 'all', cursor: 'text' }}>{p.sku || '—'}</span>
                              </td>
                              <td style={{ padding: '10px 14px', fontWeight: 600, color: '#333' }}>{p.name_en || '—'}</td>
                              <td style={{ padding: '10px 14px', direction: 'rtl', color: '#555', letterSpacing: 'normal' }}>{p.name_ar || '—'}</td>
                              <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                <StatusChip state={isOut ? 'out' : 'low'} label={isOut ? (ar ? 'نفذ' : 'Out') : String(p.stock)} style={{ fontSize: 12, display: 'inline-flex' }} />
                              </td>
                              <td style={{ padding: '10px 14px', textAlign: 'center', color: '#888', fontSize: 12 }}>{p.min_qty || 1}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Modal footer */}
                  <div style={{ padding: '14px 24px', borderTop: '1px solid var(--sedra-line)', textAlign: 'center', flexShrink: 0 }}>
                    <button onClick={() => setIsLowStockModalOpen(false)} style={{ padding: '9px 28px', borderRadius: 9, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                      {ar ? 'إغلاق' : 'Close'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: 'var(--sedra-forest-soft)' }}>{ar ? 'آخر الطلبات' : 'Recent Orders'}</h3>
        {recent.length === 0
          ? <div style={{ color: '#bbb', fontSize: 14 }}>{ar ? '\u0644\u0627 \u062a\u0648\u062c\u062f \u0637\u0644\u0628\u0627\u062a \u0628\u0639\u062f' : 'No orders yet'}</div>
          : <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ borderBottom: '2px solid var(--sedra-line)' }}>
                {['Order ID', ar ? '\u0627\u0644\u0639\u0645\u064a\u0644' : 'Customer', ...(canSeeTotals ? [ar ? '\u0627\u0644\u0645\u0628\u0644\u063a' : 'Total'] : []), ar ? '\u0627\u0644\u062d\u0627\u0644\u0629' : 'Status', ar ? '\u0627\u0644\u062a\u0627\u0631\u064a\u062e' : 'Date'].map(h => (
                  <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {recent.map(o => (
                  <tr key={o.id}
                    onClick={() => setTab && setTab('orders')}
                    style={{ borderBottom: '1px solid var(--sedra-line)', cursor: setTab ? 'pointer' : 'default' }}
                    onMouseEnter={e => { if (setTab) e.currentTarget.style.background='#f8f6f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background=''; }}
                  >
                    <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: 12, color: '#666' }}>{o.id}</td>
                    <td style={{ padding: '10px', fontWeight: 600 }}>{o.business_name || o.contact_name}</td>
                    {canSeeTotals && <td style={{ padding: '10px', fontWeight: 700, color: 'var(--sedra-forest-soft)' }}>{fmt(o.total)}</td>}
                    <td style={{ padding: '10px' }}><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc(o.status) + '22', color: sc(o.status) }}>{o.status}</span></td>
                    <td style={{ padding: '10px', color: '#aaa', fontSize: 12 }}>{(o.created_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', marginTop: 20 }}>
        <h3 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--sedra-forest-soft)', display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Link" size={16} /> {ar ? 'روابط الكتالوجات' : 'Catalogue Links'}</h3>
        <p style={{ fontSize: 12, color: '#aaa', margin: '0 0 14px' }}>{ar ? 'روابطك السرية للعملاء' : 'Your secret per-client links'}</p>
        <TokenManager lang={lang} />
      </div>
    </div>
  );
}

// ─── BANNERS ADMIN ───────────────────────────────────────────────────────────
function BannersAdmin({ lang }) {
  const ar = lang === 'ar';
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ image_url: '', link_url: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const toast = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  // Banners are full-bleed and load on every storefront visit, so they are the
  // single most bandwidth-sensitive asset in the app. Uploading through the
  // shared compressor caps them at HERO_MAX_WIDTH WebP instead of whatever
  // multi-megabyte PNG came out of a design tool.
  const HERO_MAX_WIDTH = 1920;
  const HERO_QUALITY   = 0.72;
  const { upload: uploadBanner, status: upStatus, progress: upProgress, error: upError } = useImageUpload();
  const onPickBanner = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                       // allow re-picking the same file
    if (!file) return;
    const url = await uploadBanner(file, 'banner', {
      maxWidthOrHeight: HERO_MAX_WIDTH,
      initialQuality:   HERO_QUALITY,
    });
    if (url) {
      setForm(f => ({ ...f, image_url: url }));
      toast(ar ? 'تم رفع الصورة — اضغط "إضافة" للحفظ' : 'Image uploaded — press "Add" to save');
    }
  };
  const inputS = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };

  const load = () => {
    setLoading(true);
    supaFetch('GET', 'site_banners', null, '?order=sort_order.asc')
      .then(({ data }) => setBanners(data || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!form.image_url.trim()) return;
    setSaving(true);
    try {
      await supaFetch('POST', 'site_banners', {
        image_url: form.image_url.trim(),
        link_url: form.link_url.trim() || null,
        sort_order: parseInt(form.sort_order) || 0,
        active: true,
      });
      setForm({ image_url: '', link_url: '', sort_order: 0 });
      toast(ar ? 'تمت الإضافة' : 'Banner added');
      load();
    } catch (e) { toast('Error: ' + e.message); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(ar ? 'حذف هذا البانر؟' : 'Delete this banner?')) return;
    await supaFetch('DELETE', 'site_banners', null, `?id=eq.${id}`);
    toast(ar ? 'تم الحذف' : 'Deleted');
    load();
  };

  const toggle = async (id, active) => {
    await supaFetch('PATCH', 'site_banners', { active: !active }, `?id=eq.${id}`);
    load();
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto' }}>
      {msg && <div style={{ background: 'var(--sedra-forest-soft)', color: '#fff', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {/* Add form */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 24 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{ar ? 'إضافة بانر جديد' : 'Add New Banner'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{ar ? 'رابط الصورة *' : 'Image URL *'}</label>
            <input style={inputS} placeholder="https://..." value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{ar ? 'رابط الضغط (اختياري)' : 'Click Link (optional)'}</label>
            <input style={inputS} placeholder="https://..." value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} />
          </div>
          <button onClick={save} disabled={saving || !form.image_url.trim()}
            style={{ padding: '10px 20px', borderRadius: 8, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', opacity: !form.image_url.trim() ? 0.5 : 1 }}>
            {saving ? '...' : (ar ? 'إضافة' : '+ Add')}
          </button>
        </div>
        {/* Upload path — compresses to WebP before it ever reaches Storage. */}
        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ padding: '9px 16px', borderRadius: 8, background: 'var(--sedra-forest-soft)', color: '#fff', cursor: upStatus === 'compressing' || upStatus === 'uploading' ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Icon name="Upload" size={14} />
            {upStatus === 'compressing' ? (ar ? `جارٍ الضغط… ${upProgress}%` : `Compressing… ${upProgress}%`)
              : upStatus === 'uploading' ? (ar ? `جارٍ الرفع… ${upProgress}%` : `Uploading… ${upProgress}%`)
              : (ar ? 'رفع صورة بانر' : 'Upload banner image')}
            <input type="file" accept="image/*" onChange={onPickBanner}
              disabled={upStatus === 'compressing' || upStatus === 'uploading'}
              style={{ display: 'none' }} />
          </label>
          <span style={{ fontSize: 12, color: 'var(--sedra-ink-muted)' }}>
            {ar ? `يُضغط تلقائيًا إلى ${HERO_MAX_WIDTH}px WebP` : `Auto-compressed to ${HERO_MAX_WIDTH}px WebP`}
          </span>
        </div>
        {upError && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--status-danger)' }}>{upError}</div>}
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--sedra-ink-muted)', background: '#f8f6f2', padding: '8px 12px', borderRadius: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <Icon name="Lightbulb" size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {ar ? 'استخدم زر الرفع أعلاه. لصق رابط صورة غير مضغوطة يستهلك حصة النطاق بسرعة كبيرة.'
                  : 'Use the upload button above. Pasting a URL to an uncompressed image is what exhausted the bandwidth quota before.'}
        </div>
      </div>

      {/* Banner list */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>{ar ? 'البانرات الحالية' : 'Current Banners'} ({banners.length})</h3>
        {loading ? <div style={{ color: '#aaa', textAlign: 'center', padding: 24 }}>…</div>
        : banners.length === 0
          ? <div style={{ color: '#aaa', textAlign: 'center', padding: 24, fontSize: 14 }}>{ar ? 'لا يوجد بانرات بعد' : 'No banners yet'}</div>
          : banners.map(b => (
            <div key={b.id} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '12px 0', borderBottom: '1px solid var(--sedra-line)' }}>
              <img src={b.image_url} alt="" style={{ width: 110, height: 50, objectFit: 'cover', borderRadius: 6, flexShrink: 0, background: '#eee' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.image_url}</div>
                {b.link_url && <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>{b.link_url}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button onClick={() => toggle(b.id, b.active)}
                  style={{ padding: '5px 10px', borderRadius: 6, border: '1.5px solid', fontSize: 11, fontWeight: 700, cursor: 'pointer', background: b.active ? 'var(--status-success-bg)' : 'var(--status-danger-bg)', color: b.active ? 'var(--status-success)' : 'var(--status-danger)', borderColor: 'transparent' }}>
                  {b.active ? (ar ? 'مفعّل' : 'Active') : (ar ? 'معطّل' : 'Hidden')}
                </button>
                <button onClick={() => del(b.id)}
                  style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'var(--status-danger-bg)', color: 'var(--status-danger)', display: 'flex', alignItems: 'center' }}><Icon name="Trash2" size={14} /></button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// ─── STAFF MANAGEMENT ────────────────────────────────────────────────────────
function StaffManagement({ lang, session }) {
  const ar = lang === 'ar';
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'sales' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [newPw, setNewPw] = useState('');
  // Auto-dismiss success/error banners after 4 seconds
  useEffect(() => { if (!success) return; const t = setTimeout(() => setSuccess(''), 4000); return () => clearTimeout(t); }, [success]);
  useEffect(() => { if (!err) return; const t = setTimeout(() => setErr(''), 6000); return () => clearTimeout(t); }, [err]);
  const ROLE_OPTIONS = [
    { value: 'super_admin', label: ar ? 'مشرف رئيسي' : 'Super Admin' },
    { value: 'manager',     label: ar ? 'مدير'        : 'Manager'     },
    { value: 'sales',       label: ar ? 'مبيعات'      : 'Sales'       },
    { value: 'warehouse',   label: ar ? 'مخزن'        : 'Warehouse'   },
  ];
  const loadStaff = async () => {
    setLoading(true);
    try {
      const token = await getValidToken();
      const res = await fetch(`${SUPABASE_URL}/rest/v1/staff_roles?select=*&order=created_at.asc`, {
        headers: { 'apikey': SUPABASE_KEY, 'Authorization': `Bearer ${token}` },
      });
      const rows = await res.json();
      setStaff(Array.isArray(rows) ? rows : []);
    } catch(e) { setErr(ar ? 'تعذّر تحميل الموظفين' : 'Failed to load staff: ' + e.message); }
    setLoading(false);
  };
  useEffect(() => { loadStaff(); }, []);
  const callFn = async (body) => {
    const token = await getValidToken();
    if (!token) throw new Error('Session expired — please log in again');
    const res = await fetch(STAFF_FN, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed');
    return data;
  };
  const addStaff = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setSuccess('');
    try {
      await callFn({ action: 'create', ...form });
      setSuccess(ar ? 'تم إضافة الموظف بنجاح' : 'Staff member added successfully!');
      setForm({ name:'', email:'', password:'', role:'sales' });
      await loadStaff();
    } catch(e) { setErr(e.message); }
    setBusy(false);
  };
  const updateStaff = async (id, updates) => {
    setBusy(true);
    try {
      const token = await getValidToken();
      await fetch(`${SUPABASE_URL}/rest/v1/staff_roles?id=eq.${id}`, {
        method: 'PATCH',
        headers: { 'apikey': SUPABASE_KEY, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Prefer': 'return=representation' },
        body: JSON.stringify(updates),
      });
      await loadStaff();
    } catch(e) { setErr(e.message); }
    setBusy(false);
  };
  const deleteStaff = async (id, name) => {
    if (!window.confirm(ar ? `هل أنت متأكد من حذف ${name}؟ لا يمكن التراجع.` : `Delete ${name}? This cannot be undone.`)) return;
    setBusy(true); setErr(''); setSuccess('');
    try {
      await callFn({ action: 'delete', id });
      setSuccess(ar ? 'تم حذف الموظف بنجاح' : 'Staff member deleted.');
      await loadStaff();
    } catch(e) { setErr(e.message); }
    setBusy(false);
  };
  const doResetPassword = async (id) => {
    if (!newPw || newPw.length < 6) { setErr(ar ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters'); return; }
    setBusy(true); setErr(''); setSuccess('');
    try {
      await callFn({ action: 'reset_password', id, password: newPw });
      setSuccess(ar ? 'تم تغيير كلمة المرور بنجاح' : 'Password reset successfully!');
      setResetTarget(null);
      setNewPw('');
    } catch(e) { setErr(e.message); }
    setBusy(false);
  };
  const inp = { width:'100%', padding:'10px 12px', borderRadius:8, border:'1.5px solid var(--sedra-line)', fontSize:14, boxSizing:'border-box', outline:'none', background:'#fff', fontFamily:'Tajawal, sans-serif' };
  return (
    <div>
      <h2 style={{ margin:'0 0 20px', color:'var(--sedra-forest)', fontSize:22, fontWeight:800 }}>{ar ? 'إدارة الموظفين' : 'Staff Management'}</h2>
      {/* Add staff form */}
      <div style={{ background:'#fff', borderRadius:14, padding:'24px', marginBottom:24, boxShadow:'0 2px 12px rgba(74,82,64,0.08)' }}>
        <h3 style={{ margin:'0 0 16px', fontSize:16, color:'var(--sedra-forest-soft)', fontWeight:700 }}>{ar ? 'إضافة موظف جديد' : 'Add New Staff Member'}</h3>
        <form onSubmit={addStaff}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <input type="text" placeholder={ar ? 'الاسم الكامل' : 'Full Name'} value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} style={inp} required />
            <input type="email" placeholder={ar ? 'البريد الإلكتروني' : 'Email'} value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} style={inp} required />
            <input type="password" placeholder={ar ? 'كلمة مرور مؤقتة' : 'Temporary Password'} value={form.password} onChange={e=>setForm(f=>({...f,password:e.target.value}))} style={inp} required minLength={6} />
            <select value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} style={{...inp}}>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          {err     && <div style={{background:'var(--status-danger-bg)',border:'1px solid var(--status-danger-bg)',borderRadius:7,padding:'8px 12px',marginBottom:10,fontSize:13,color:'var(--status-danger)'}}>{err}</div>}
          {success && <div style={{background:'#f0fdf4',border:'1px solid #86efac',borderRadius:7,padding:'8px 12px',marginBottom:10,fontSize:13,color:'var(--status-success)'}}>{success}</div>}
          <button type="submit" disabled={busy}
            style={{padding:'10px 24px',borderRadius:8,background:busy?'#aaa':'var(--sedra-olive)',color:'#fff',fontWeight:700,fontSize:14,border:'none',cursor:busy?'not-allowed':'pointer'}}>
            {busy ? (ar?'جارٍ الإضافة...':'Adding…') : (ar?'إضافة موظف':'+ Add Staff Member')}
          </button>
        </form>
      </div>
      {/* Staff list */}
      <div style={{ background:'#fff', borderRadius:14, padding:'24px', boxShadow:'0 2px 12px rgba(74,82,64,0.08)' }}>
        <h3 style={{ margin:'0 0 16px', fontSize:16, color:'var(--sedra-forest-soft)', fontWeight:700 }}>{ar ? 'الموظفون الحاليون' : 'Current Staff'} ({staff.length})</h3>
        {loading ? <div style={{color:'#aaa',fontSize:14}}>Loading…</div> : staff.length === 0 ? <div style={{color:'#aaa',fontSize:14}}>No staff yet.</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {staff.map(s => {
              const b = ROLE_BADGE[s.role] || ROLE_BADGE.sales;
              const isSelf = s.email === session.email;
              return (
                <div key={s.id}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', borderRadius:resetTarget===s.id?'10px 10px 0 0':'10px', border:'1.5px solid var(--sedra-line)', borderBottom: resetTarget===s.id?'none':'1.5px solid var(--sedra-line)', background:s.active?'#fff':'#fafafa', opacity:s.active?1:0.55 }}>
                    <div style={{ width:42, height:42, borderRadius:'50%', background:'var(--sedra-forest-soft)', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:800, fontSize:17, flexShrink:0 }}>
                      {(s.name||'?').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:14, color:'var(--sedra-forest)' }}>{s.name} {isSelf && <span style={{fontSize:11,color:'#888'}}>(you)</span>}</div>
                      <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{s.email}</div>
                    </div>
                    <select value={s.role} disabled={isSelf || busy}
                      onChange={e => updateStaff(s.id, { role: e.target.value })}
                      style={{ padding:'6px 10px', borderRadius:8, border:'1.5px solid var(--sedra-line)', fontSize:13, background:b.bg, color:b.color, fontWeight:700, cursor:isSelf?'not-allowed':'pointer' }}>
                      {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button disabled={isSelf || busy} onClick={() => updateStaff(s.id, { active: !s.active })}
                      style={{ padding:'6px 12px', borderRadius:8, border:'none', cursor:isSelf?'not-allowed':'pointer', background:s.active?'var(--status-danger-bg)':'var(--status-success-bg)', color:s.active?'var(--status-danger)':'var(--status-success)', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                      {s.active ? (ar?'تعطيل':'Disable') : (ar?'تفعيل':'Enable')}
                    </button>
                    <button disabled={isSelf || busy} onClick={() => { setResetTarget(resetTarget===s.id?null:s.id); setNewPw(''); setErr(''); }}
                      style={{ padding:'6px 12px', borderRadius:8, border:'none', cursor:isSelf?'not-allowed':'pointer', background: resetTarget===s.id?'#dbeafe':'var(--status-info-bg)', color:'var(--status-info)', fontSize:12, fontWeight:700, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:5 }}>
                      <Icon name="KeyRound" size={13} /> {ar ? 'كلمة مرور' : 'Reset PW'}
                    </button>
                    <button disabled={isSelf || busy} onClick={() => deleteStaff(s.id, s.name)}
                      style={{ padding:'6px 12px', borderRadius:8, border:'none', cursor:isSelf?'not-allowed':'pointer', background:'var(--status-danger-bg)', color:'var(--status-danger)', fontSize:12, fontWeight:700, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:5 }}>
                      <Icon name="Trash2" size={13} /> {ar ? 'حذف' : 'Delete'}
                    </button>
                  </div>
                  {resetTarget === s.id && (
                    <div style={{ padding:'12px 16px', borderRadius:'0 0 10px 10px', border:'1.5px solid var(--sedra-line)', borderTop:'none', background:'var(--status-info-bg)', display:'flex', gap:8, alignItems:'center' }}>
                      <input type="password" placeholder={ar ? 'كلمة المرور الجديدة (6 أحرف على الأقل)' : 'New password (min 6 chars)'}
                        value={newPw} onChange={e=>setNewPw(e.target.value)} minLength={6}
                        style={{ flex:1, padding:'8px 10px', borderRadius:7, border:'1.5px solid #bfdbfe', fontSize:13, outline:'none', fontFamily:'Tajawal, sans-serif', background:'#fff' }} />
                      <button onClick={() => doResetPassword(s.id)} disabled={busy}
                        style={{ padding:'8px 18px', borderRadius:7, background:'var(--status-info)', color:'#fff', fontWeight:700, fontSize:13, border:'none', cursor:busy?'not-allowed':'pointer', whiteSpace:'nowrap' }}>
                        {busy ? '…' : (ar ? 'حفظ' : 'Save')}
                      </button>
                      <button onClick={() => { setResetTarget(null); setNewPw(''); }}
                        style={{ padding:'8px 12px', borderRadius:7, background:'#e0e7ef', color:'#555', fontWeight:700, border:'none', cursor:'pointer', display:'flex', alignItems:'center' }}>
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── ADMIN LOGIN ──────────────────────────────────────────────────────────────
function AdminLogin({ onLogin, lang, setLang }) {
  const t = T[lang];
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const [email, setEmail] = useState('');
  const [pw,    setPw]    = useState('');
  const [err,   setErr]   = useState('');
  const [busy,  setBusy]  = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr('');
    try {
      const data = await auth.signIn(email.trim(), pw);
      const roleRow = await auth.getRole(data.user.id, data.access_token);
      // Marketplace: non-staff logins may be approved vendors
      const vendorRow = roleRow ? null : await auth.getVendor(data.user.id, data.access_token);
      if (!roleRow && !vendorRow) throw new Error(lang === 'ar' ? 'ليس لديك صلاحية الوصول — تواصل مع المشرف' : 'Access denied — contact your admin');
      const account = roleRow
        ? { role: roleRow.role, name: roleRow.name, email: roleRow.email }
        : { role: 'vendor', name: vendorRow.business_name_en, email: vendorRow.email, vendor: vendorRow };
      // role/name/email are persisted alongside the tokens: the catalogue route
      // reads them to detect a staff session for "View as customer" (?admin=1)
      // without a network round-trip. getValidToken() spreads ...stored on
      // refresh, so these fields survive token rotation.
      localStorage.setItem(SESSION_KEY, JSON.stringify({
        access_token:  data.access_token,
        refresh_token: data.refresh_token,
        user_id:       data.user.id,
        expires_at:    Date.now() + ((data.expires_in || 3600) - 60) * 1000,
        role:  account.role,
        name:  account.name,
        email: account.email,
      }));
      posthog.identify(data.user.id, {
        email: account.email,
        name: account.name,
        role: account.role,
      });
      posthog.capture('admin_login_succeeded', { role: account.role });
      onLogin(account);
    } catch(e) {
      posthog.capture('admin_login_failed', { reason: e?.name || 'authentication_error' });
      posthog.captureException(e, { flow: 'admin_login' });
      setErr(e.message);
    }
    setBusy(false);
  };
  const inp = { width:'100%', padding:'12px 14px', borderRadius:8, border:'1.5px solid var(--sedra-line)', fontSize:15, boxSizing:'border-box', outline:'none', background:'#fff', fontFamily:'Tajawal, sans-serif', direction:'ltr' };
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(160deg,#1f271c 0%,#2c3527 40%,#3A4534 72%,#4c5845 100%)', fontFamily:'Tajawal, sans-serif', direction:dir, position:'relative', overflow:'hidden' }}>
      <style>{`@keyframes sdra-shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-6px)}40%,80%{transform:translateX(6px)}}.sdra-shake{animation:sdra-shake 0.4s ease}@keyframes sdra-drift1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(40px,-30px) scale(1.15)}}`}</style>
      <div style={{ position:'absolute', width:420, height:420, borderRadius:'50%', filter:'blur(70px)', background:'radial-gradient(circle,rgba(139,160,110,0.20) 0%,transparent 65%)', top:'16%', left:'30%', animation:'sdra-drift1 9s ease-in-out infinite', pointerEvents:'none' }}/>
      <div style={{ background:'rgba(255,255,255,0.96)', backdropFilter:'blur(14px)', borderRadius:18, padding:'40px 36px', boxShadow:'0 24px 70px rgba(0,0,0,0.35)', border:'1px solid rgba(255,255,255,0.4)', width:360, textAlign:'center', position:'relative' }}>
        <img src={LOGO_URL} alt="Sedra Home" style={{ width:100, height:'auto', borderRadius:10, marginBottom:14 }} />
        <div style={{ color:'#888', fontSize:12, marginBottom:28, letterSpacing:lang==='ar'?'normal':1.5, textTransform:lang==='ar'?'none':'uppercase' }}>{t.adminPanel}</div>
        <form onSubmit={submit} className={err ? 'sdra-shake' : ''}>
          <input type="email" placeholder={lang==='ar'?'البريد الإلكتروني':'Email address'} value={email}
            onChange={e=>{setEmail(e.target.value);setErr('');}} disabled={busy} style={{...inp,marginBottom:10}} required />
          <input type="password" placeholder={t.password} value={pw}
            onChange={e=>{setPw(e.target.value);setErr('');}} disabled={busy} style={{...inp,marginBottom:10}} required />
          {err && <div style={{background:'var(--status-danger-bg)',border:'1px solid var(--status-danger-bg)',borderRadius:7,padding:'8px 12px',marginBottom:10,fontSize:13,color:'var(--status-danger)',textAlign:'left'}}>{err}</div>}
          <button type="submit" disabled={busy}
            style={{width:'100%',padding:'12px',borderRadius:8,background:busy?'#aaa':'var(--sedra-olive)',color:'#fff',fontWeight:700,fontSize:15,border:'none',cursor:busy?'not-allowed':'pointer',transition:'background 0.2s'}}>
            {busy ? (lang==='ar'?'جارٍ تسجيل الدخول...':'Signing in…') : t.signIn}
          </button>
        </form>
        <div style={{marginTop:20,display:'flex',justifyContent:'center'}}>
          <LangToggle lang={lang} setLang={setLang} variant="light" size="sm" />
        </div>
      </div>
    </div>
  );
}
// ─── ADMIN SHELL ──────────────────────────────────────────────────────────────
// Merge identity fields into the persisted session without touching the tokens.
// Backfills sessions created before role/name/email were persisted, and keeps
// the copy fresh after a role change.
function persistSessionFields(fields) {
  try {
    const cur = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (cur) localStorage.setItem(SESSION_KEY, JSON.stringify({ ...cur, ...fields }));
  } catch {}
}

function AdminShell() {
  // ── Tab: persisted in sessionStorage so refresh lands on the same tab ──────
  const [tab, setTabRaw] = useState(
    () => sessionStorage.getItem('sedra_admin_tab') || 'dashboard'
  );
  const setTab = (t) => { sessionStorage.setItem('sedra_admin_tab', t); setTabRaw(t); };

  const [lang,           setLang]           = useState(() => localStorage.getItem('sedra_lang') || 'ar');
  const [session,        setSession]        = useState(null);   // { role, name, email }
  const [isAuthLoading,  setIsAuthLoading]  = useState(true);  // true until session check resolves
  const [viewAsVendor,   setViewAsVendor]   = useState(null);  // vendor row being impersonated
  const isMobile = useIsMobile();
  const t = T[lang];

  // Restore session from localStorage on mount — use getValidToken() to refresh if near expiry.
  // isAuthLoading stays true until this effect fully resolves so we never flash the login screen.
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    if (!stored) { setIsAuthLoading(false); return; }
    if (Date.now() > stored.expires_at) {
      localStorage.removeItem(SESSION_KEY);
      setIsAuthLoading(false);
      return;
    }
    getValidToken()
      .then(token => {
        if (!token) { localStorage.removeItem(SESSION_KEY); setIsAuthLoading(false); return; }
        const updated = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
        if (!updated) { setIsAuthLoading(false); return; }
        return auth.getRole(updated.user_id, token)
          .then(async row => {
            if (row) {
              posthog.identify(updated.user_id, { email: row.email, name: row.name, role: row.role });
              setSession({ role: row.role, name: row.name, email: row.email });
              persistSessionFields({ role: row.role, name: row.name, email: row.email });
              return;
            }
            // Marketplace: fall back to vendor session
            const vendorRow = await auth.getVendor(updated.user_id, token);
            if (vendorRow) {
              posthog.identify(updated.user_id, { email: vendorRow.email, name: vendorRow.business_name_en, role: 'vendor' });
              setSession({ role: 'vendor', name: vendorRow.business_name_en, email: vendorRow.email, vendor: vendorRow });
              persistSessionFields({ role: 'vendor', name: vendorRow.business_name_en, email: vendorRow.email });
            }
            else localStorage.removeItem(SESSION_KEY);
          })
          .catch(() => localStorage.removeItem(SESSION_KEY));
      })
      .catch(() => localStorage.removeItem(SESSION_KEY))
      .finally(() => setIsAuthLoading(false));
  }, []);

  // Periodic session validity check — auto-logout if token cannot be refreshed
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(async () => {
      const token = await getValidToken();
      if (!token) {
        localStorage.removeItem(SESSION_KEY);
        setSession(null);
      }
    }, 5 * 60 * 1000); // every 5 minutes
    return () => clearInterval(interval);
  }, [session]);

  // Persist language preference across sessions
  useEffect(() => { localStorage.setItem('sedra_lang', lang); }, [lang]);

  // Audit each "View as vendor" entry, mirroring the customer-side log. Failure
  // is surfaced to the console rather than swallowed — an unlogged
  // impersonation is the one thing this table exists to prevent.
  useEffect(() => {
    if (!viewAsVendor) return;
    const stored = readSession();
    supaFetch('POST', 'admin_impersonation_log', {
      kind: 'vendor',
      admin_user_id: stored?.user_id || null,
      admin_email: session?.email || null,
      admin_name:  session?.name  || null,
      admin_role:  session?.role  || null,
      vendor_id: viewAsVendor.id,
      account_label: viewAsVendor.business_name_en || viewAsVendor.business_name_ar || null,
    }, '', { Prefer: 'return=minimal' })
      .catch(e => console.warn('[impersonation-audit] vendor session not recorded:', e?.message));
  }, [viewAsVendor, session]);

  const handleLogout = async () => {
    const stored = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
    posthog.capture('admin_logout', { role: session?.role || 'unknown' });
    if (stored?.access_token) await auth.signOut(stored.access_token);
    localStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem('sedra_admin_tab');
    posthog.reset();
    setSession(null); setTab('dashboard');
  }; 

  // ── Auth gate ──────────────────────────────────────────────────────────────
  // Show a minimal spinner while the localStorage session is being verified.
  // This replaces the login-screen flash on refresh.
  if (isAuthLoading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(155deg,#242c21 0%,#3A4534 38%,#4c5845 68%,#5e6c50 100%)',
    }}>
      <img
        src={LOGO_URL}
        alt="Sedra Home"
        style={{ width: 64, marginBottom: 28, opacity: 0.9, filter: 'drop-shadow(0 4px 14px rgba(0,0,0,0.4))' }}
      />
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(255,255,255,0.15)', borderTopColor: 'rgba(205,220,185,0.85)', animation: 'sdra-spin 0.7s linear infinite' }} />
      <style>{'@keyframes sdra-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  if (!session) return <AdminLogin onLogin={s => setSession(s)} lang={lang} setLang={setLang} />;

  // Marketplace: vendors get their own portal, never the admin dashboard
  if (session.role === 'vendor') {
    return <VendorPortal vendor={session.vendor} lang={lang} setLang={setLang} onLogout={handleLogout} />;
  }

  // "View as vendor" — render the vendor's OWN portal in place of the admin
  // dashboard. Same component the vendor signs into, so it never drifts.
  if (viewAsVendor) {
    return <VendorPortal vendor={viewAsVendor} lang={lang} setLang={setLang}
             onLogout={handleLogout}
             impersonatedBy={session}
             onExitImpersonation={() => setViewAsVendor(null)} />;
  }

  const { role, name } = session;
  const allowedTabs = ROLE_TABS[role] || ['orders'];
  const activeTab   = allowedTabs.includes(tab) ? tab : allowedTabs[0];
  const badge       = ROLE_BADGE[role] || ROLE_BADGE.sales;
  const dir         = lang === 'ar' ? 'rtl' : 'ltr';

  const navItems = [
    { key:'dashboard',    label:t.dashboard,                               icon:'LayoutDashboard' },
    { key:'products',     label:t.products,                                icon:'Package' },
    { key:'customers',    label:t.customers,                               icon:'Users' },
    { key:'orders',       label:t.orders,                                  icon:'ShoppingBag' },
    { key:'applications', label:lang==='ar'?'طلبات الجملة':'Applications', icon:'ClipboardList' },
    { key:'vendors',      label:lang==='ar'?'البائعون':'Vendors',          icon:'Store' },
    { key:'banners',      label:lang==='ar'?'البانرات':'Banners',          icon:'Image' },
    { key:'analytics',    label:lang==='ar'?'التحليلات':'Analytics',       icon:'ChartColumn' },
    { key:'settings',     label:t.settings,                                icon:'Settings' },
    { key:'staff',        label:lang==='ar'?'الموظفون':'Staff',            icon:'User' },
  ].filter(item => allowedTabs.includes(item.key));

  // newOrderCount is a lightweight Badge — Orders component owns the real data.
  // We use a cheap separate query just for the badge count.
  const newOrderCount = 0; // Will be replaced in Phase 6 with live cache read.

  const renderContent = () => (
    <>
      {activeTab==='dashboard'    && <Dashboard    lang={lang} setTab={setTab} canSeeTotals={CAN_SEE_TOTALS[role]} role={role} />}
      {activeTab==='products'     && <Products     lang={lang} />}
      {activeTab==='customers'    && <Customers    lang={lang} />}
      {activeTab==='orders'       && <Orders       lang={lang} canStatus={CAN_STATUS[role]} canSeeTotals={CAN_SEE_TOTALS[role]} isSuperAdmin={role === 'super_admin'} />}
      {activeTab==='applications' && <Applications lang={lang} />}
      {activeTab==='vendors'      && <Vendors      lang={lang} onViewAsVendor={setViewAsVendor} />}
      {activeTab==='banners'      && <BannersAdmin  lang={lang} />}
      {activeTab==='analytics'    && <Analytics     lang={lang} />}
      {activeTab==='settings'     && <Settings     lang={lang} setLang={setLang} />}
      {activeTab==='staff'        && <StaffManagement lang={lang} session={session} />}
    </>
  );

  return (
    <div style={{minHeight:'100vh',background:'var(--sedra-cream)',fontFamily:'var(--font-body)',direction:dir}}>
      {isMobile ? (
        <div style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
          {/* Mobile top bar */}
          <div style={{background:'var(--sedra-nav)',color:'var(--sedra-ink)',padding:'8px 16px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:100,borderBottom:'1px solid var(--sedra-line)'}}>
            <img src={LOGO_URL} alt="Sedra Home" style={{height:36,width:'auto',borderRadius:6}} />
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              <span style={{fontSize:10,padding:'2px 8px',borderRadius:10,background:badge.bg,color:badge.color,fontWeight:700}}>{badge.label}</span>
              <LangToggle lang={lang} setLang={setLang} variant="light" size="sm" />
              <button onClick={handleLogout} style={{background:'var(--sedra-olive)',border:'none',color:'var(--sedra-cream)',padding:'6px 10px',borderRadius:6,cursor:'pointer',display:'flex'}}><Icon name="LogOut" size={14} /></button>
            </div>
          </div>
          <div style={{flex:1,padding:'16px',paddingBottom:80,overflowY:'auto'}}>{renderContent()}</div>
          {/* Mobile bottom nav */}
          <div style={{position:'fixed',bottom:0,left:0,right:0,background:'var(--sedra-nav)',display:'flex',borderTop:'1px solid var(--sedra-line)',zIndex:100}}>
            {navItems.map(item => (
              <button key={item.key} onClick={()=>setTab(item.key)}
                style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'8px 4px',background:'none',border:'none',cursor:'pointer',color:activeTab===item.key?'var(--sedra-pistachio-deep)':'var(--sedra-ink-muted)',position:'relative'}}>
                <Icon name={item.icon} size={20} />
                <span style={{fontSize:10,marginTop:2,fontWeight:activeTab===item.key?700:400}}>{item.label}</span>
                {item.key==='orders'&&newOrderCount>0&&(
                  <span style={{position:'absolute',top:6,right:'18%',background:'var(--status-danger)',color:'#fff',borderRadius:'50%',width:16,height:16,fontSize:10,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700}}>{newOrderCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div style={{display:'flex',minHeight:'100vh'}}>
          <AdminSidebar
            navItems={navItems} activeTab={activeTab} onNav={setTab}
            name={name} badge={badge} lang={lang} setLang={setLang}
            onLogout={handleLogout} logoutLabel={t.logout}
            newOrderCount={newOrderCount}
          />
          {/* Main content */}
          <div style={{flex:1,padding:'32px',overflowY:'auto'}}>{renderContent()}</div>
        </div>
      )}
    </div>
  );
}
// ─── TOKEN MANAGER ───────────────────────────────────────────────────────────
function TokenManager({ lang }) {
  const ar = lang === 'ar';
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState('');
  const [tokenType, setTokenType] = useState('standard');
  const [expiryDays, setExpiryDays] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [creating, setCreating] = useState(false);
  const [tmError, setTmError] = useState('');
  // ── Cart Impersonation ─────────────────────────────────────────────────────
  const [cartModal, setCartModal]   = useState(null); // { token, label } | null
  const [cartItems, setCartItems]   = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartSaving, setCartSaving]   = useState(false);
  const [cartAddSku, setCartAddSku]   = useState('');
  const [cartMsg, setCartMsg]         = useState('');
  // Async header builder — uses refreshed token
  const getHeaders = async (extra = {}) => {
    const token = await getValidToken();
    const h = { apikey: SUPABASE_KEY, Accept: 'application/json', ...extra };
    if (token) h.Authorization = 'Bearer ' + token;
    return h;
  };
  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens?order=created_at.desc&select=*`, { headers: await getHeaders() });
      const rows = await r.json();
      setTokens(Array.isArray(rows) ? rows : []);
    } catch(e) { setTokens([]); setTmError(ar ? 'تعذّر تحميل الروابط' : 'Failed to load tokens: ' + e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!label.trim()) return;
    setCreating(true); setTmError('');
    const tok = Math.random().toString(36).slice(2,10) + Math.random().toString(36).slice(2,10);
    const body = { token: tok, label: label.trim(), type: tokenType, active: true };
    if (expiryDays) body.expires_at = new Date(Date.now() + parseInt(expiryDays) * 86400000).toISOString();
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens`, {
        method: 'POST',
        headers: await getHeaders({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
        body: JSON.stringify(body)
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message || e.hint || `Error ${r.status}`); }
      setLabel(''); setExpiryDays('');
      await load();
    } catch(e) { setTmError(e.message); }
    setCreating(false);
  };
  const revoke = async (tok) => {
    setTmError('');
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens?token=eq.${tok}`, {
        method: 'PATCH',
        headers: await getHeaders({ 'Content-Type': 'application/json', Prefer: 'return=representation' }),
        body: JSON.stringify({ active: false })
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message || e.hint || `Error ${r.status}`); }
      load();
    } catch(e) { setTmError(e.message); }
  };
  const deleteToken = async (tok, lbl) => {
    if (!window.confirm(ar ? `حذف رابط "${lbl}" نهائياً؟` : `Permanently delete link "${lbl}"?`)) return;
    setTmError('');
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens?token=eq.${tok}`, {
        method: 'DELETE',
        headers: await getHeaders(),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.message || e.hint || `Error ${r.status}`); }
      load();
    } catch(e) { setTmError(e.message); }
  };
  const copyUrl = (tok) => {
    navigator.clipboard.writeText(catalogueUrl(tok)).then(() => { setCopied(tok); setTimeout(() => setCopied(null), 2200); });
  };
  // ── Cart Impersonation helpers ─────────────────────────────────────────────
  const openCartModal = async (tk) => {
    setCartModal({ token: tk.token, label: tk.label });
    setCartItems([]); setCartMsg(''); setCartAddSku('');
    setCartLoading(true);
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/saved_carts?token=eq.${encodeURIComponent(tk.token)}&select=items`, {
        headers: await getHeaders(),
      });
      const rows = await r.json();
      setCartItems(Array.isArray(rows[0]?.items) ? rows[0].items : []);
    } catch { setCartItems([]); }
    setCartLoading(false);
  };
  const saveCartModal = async () => {
    if (!cartModal) return;
    setCartSaving(true); setCartMsg('');
    try {
      const h = await getHeaders({ 'Content-Type': 'application/json', Prefer: 'return=minimal' });
      // Upsert saved_carts
      await fetch(`${SUPABASE_URL}/rest/v1/saved_carts`, {
        method: 'POST', headers: { ...h, Prefer: 'resolution=merge-duplicates,return=minimal' },
        body: JSON.stringify({ token: cartModal.token, items: cartItems, updated_at: new Date().toISOString() }),
      });
      // Audit log to outbox_events
      await fetch(`${SUPABASE_URL}/rest/v1/outbox_events`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ event_type: 'admin_cart_edit', payload: { token: cartModal.token, label: cartModal.label, item_count: cartItems.length, edited_at: new Date().toISOString() }, webhook_url: '' }),
      });
      setCartMsg('OK:' + (ar ? 'تم الحفظ' : 'Saved'));
    } catch (e) { setCartMsg('ERR:' + e.message); }
    setCartSaving(false);
  };
  const cartAddBySkuModal = async () => {
    const skuRaw = cartAddSku.trim().toUpperCase();
    if (!skuRaw) return;
    try {
      const r = await fetch(`${SUPABASE_URL}/rest/v1/products?sku=eq.${encodeURIComponent(skuRaw)}&is_active=eq.true&select=id,sku,name_en,name_ar,price,min_qty,image&limit=1`, {
        headers: await getHeaders(),
      });
      const rows = await r.json();
      if (!rows?.length) { setCartMsg(ar ? 'ERR:SKU غير موجود' : 'ERR:SKU not found'); return; }
      const p = rows[0];
      const key = `${p.id}:`;
      const exists = cartItems.find(i => i._key === key);
      if (exists) {
        setCartItems(prev => prev.map(i => i._key === key ? { ...i, qty: i.qty + (p.min_qty || 1) } : i));
      } else {
        setCartItems(prev => [...prev, { _key: key, id: p.id, sku: p.sku, name_en: p.name_en, name_ar: p.name_ar, price: p.price, qty: p.min_qty || 1, image: p.image, min_qty: p.min_qty || 1, vendor_id: p.vendor_id || null, vendor_name: p.vendor_name_en || null }]);
      }
      setCartAddSku(''); setCartMsg('');
    } catch (e) { setCartMsg('ERR:' + e.message); }
  };
  const Btn = ({ onClick, bg, color, children, disabled }) => (
    <button onClick={onClick} disabled={disabled} style={{ padding:'5px 10px', borderRadius:6, background: disabled ? '#ccc' : bg, color, border:'none', cursor: disabled ? 'not-allowed' : 'pointer', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>{children}</button>
  );
  const inp = { padding:'7px 10px', borderRadius:7, border:'1px solid #ddd', fontSize:13, outline:'none', fontFamily:'inherit' };
  return (
    <div>
      {tmError && <div style={{ background:'var(--status-danger-bg)', border:'1px solid var(--status-danger-bg)', borderRadius:7, padding:'8px 12px', marginBottom:10, fontSize:12, color:'var(--status-danger)' }}>{tmError}</div>}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:14, alignItems:'center' }}>
        <input value={label} onChange={e => setLabel(e.target.value)}
          placeholder={ar ? 'اسم العميل (مثال: ميترو القاهرة)' : 'Client label (e.g. Metro Cairo)'}
          style={{ ...inp, flex:1, minWidth:160 }} onKeyDown={e => e.key==='Enter' && create()} />
        <select value={tokenType} onChange={e => setTokenType(e.target.value)} style={{ ...inp }}>
          <option value='standard'>{ar ? 'عادي (بدون ضريبة)' : 'Standard (No VAT)'}</option>
          <option value='vat'>{ar ? 'مع ضريبة 14%' : 'With VAT +14%'}</option>
          <option value='noprice'>{ar ? 'تصفح بدون أسعار' : 'Browse Only (No Prices)'}</option>
        </select>
        <select value={expiryDays} onChange={e => setExpiryDays(e.target.value)} style={{ ...inp }}>
          <option value=''>{ar ? 'لا ينتهي' : 'No expiry'}</option>
          <option value='7'>{ar ? '7 أيام' : '7 days'}</option>
          <option value='30'>{ar ? '30 يوم' : '30 days'}</option>
          <option value='90'>{ar ? '90 يوم' : '90 days'}</option>
        </select>
        <button onClick={create} disabled={creating || !label.trim()}
          style={{ padding:'7px 16px', borderRadius:7, background: creating||!label.trim() ? '#ccc':'var(--sedra-olive)', color:'#fff', border:'none', cursor: creating||!label.trim() ? 'not-allowed':'pointer', fontSize:13, fontWeight:700 }}>
          {creating ? '…' : (ar ? '+ إنشاء رابط' : '+ Create Link')}
        </button>
      </div>
      {loading ? (
        <div style={{ color:'#aaa', fontSize:13, padding:'8px 0' }}>{ar ? 'جاري التحميل…' : 'Loading…'}</div>
      ) : tokens.length === 0 ? (
        <div style={{ color:'#bbb', fontSize:13, textAlign:'center', padding:'16px 0' }}>{ar ? 'لا توجد روابط بعد — أنشئ رابطاً أعلاه' : 'No links yet — create one above'}</div>
      ) : tokens.map(tk => {
        const url = catalogueUrl(tk.token);
        const active = tk.active;
        const expiredSoon = tk.expires_at && new Date(tk.expires_at) < new Date(Date.now() + 7 * 86400000);
        const expiredNow  = tk.expires_at && new Date(tk.expires_at) < new Date();
        return (
          <div key={tk.token} style={{ marginBottom:8, background: active && !expiredNow ? '#f9f7f4' : '#f0f0f0', borderRadius:9, padding:'10px 12px', opacity: active && !expiredNow ? 1 : 0.6 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:6, marginBottom:5 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontWeight:700, fontSize:13, color:'var(--sedra-forest-soft)' }}>{tk.label}</span>
                {/* VAT type toggle — inline segmented control */}
                {active && !expiredNow && (
                  <div style={{ display:'flex', borderRadius:6, overflow:'hidden', border:'1px solid var(--sedra-line)', flexShrink:0 }}>
                    {[['noprice', ar?'بدون سعر':'No Price'], ['standard', ar?'عادي':'Standard'], ['vat', ar?'+14%':'+14% VAT']].map(([typ, lbl]) => (
                      <button key={typ} onClick={async () => {
                        try {
                          const r = await fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens?token=eq.${tk.token}`, {
                            method:'PATCH', headers: await getHeaders({'Content-Type':'application/json','Prefer':'return=minimal'}),
                            body: JSON.stringify({ type: typ }),
                          });
                          if (!r.ok) throw new Error('Update failed');
                          load();
                        } catch (e) { setTmError(e.message); }
                      }} style={{ padding:'3px 8px', border:'none', cursor:'pointer', fontSize:10, fontWeight:700,
                        background: tk.type===typ ? (typ==='vat'?'var(--status-success)':typ==='noprice'?'#7c3aed':'var(--sedra-olive)') : '#f7f5f0',
                        color: tk.type===typ ? '#fff' : '#888',
                        borderRight: '1px solid var(--sedra-line)',
                      }}>{lbl}</button>
                    ))}
                  </div>
                )}
                {!active && <span style={{ fontSize:11, color:'var(--status-danger)', fontWeight:700, background:'var(--status-danger-bg)', padding:'1px 6px', borderRadius:4 }}>{ar ? 'مُلغى' : 'Revoked'}</span>}
                {expiredNow && <span style={{ fontSize:11, color:'var(--status-warn-dark)', fontWeight:700, background:'var(--status-warn-bg)', padding:'1px 6px', borderRadius:4 }}>{ar ? 'منتهي' : 'Expired'}</span>}
                {!expiredNow && expiredSoon && tk.expires_at && <span style={{ fontSize:11, color:'#b45309', fontWeight:600, display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="TriangleAlert" size={11} /> {ar ? 'ينتهي ' : 'Expires '}{new Date(tk.expires_at).toLocaleDateString()}</span>}
                {!expiredNow && !expiredSoon && tk.expires_at && <span style={{ fontSize:11, color:'#888', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Icon name="RefreshCw" size={11} /> {new Date(tk.expires_at).toLocaleDateString()}</span>}
              </div>
              <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                <Btn onClick={() => copyUrl(tk.token)} bg={copied===tk.token ? 'var(--status-success)':'var(--sedra-olive)'} color='#fff'>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Icon name={copied===tk.token ? 'Check' : 'ClipboardList'} size={11} /> {copied===tk.token ? (ar ? 'تم' : 'Copied') : (ar ? 'نسخ' : 'Copy')}
                  </span>
                </Btn>
                {/* Only usable tokens get the impersonation link — a revoked or
                    expired one just lands on the invalid-token screen. */}
                {active && !expiredNow && <ViewAsCustomerLink token={tk.token} lang={lang} compact />}
                {active && !expiredNow && <Btn onClick={() => revoke(tk.token)} bg='var(--status-danger-bg)' color='var(--status-danger)'><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Ban" size={11} /> {ar ? 'إلغاء' : 'Revoke'}</span></Btn>}
                {active && <Btn onClick={() => openCartModal(tk)} bg='var(--status-info-bg)' color='var(--status-info)'><span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="Eye" size={11} /> {ar ? 'السلة' : 'Cart'}</span></Btn>}
                <Btn onClick={() => deleteToken(tk.token, tk.label)} bg='#fff0f0' color='var(--status-danger)'><span style={{ display: 'flex' }}><Icon name="Trash2" size={11} /></span></Btn>
              </div>
            </div>
            <div style={{ fontSize:11, color:'#aaa', fontFamily:'monospace', wordBreak:'break-all' }}>{url}</div>
          </div>
        );
      })}

      {/* ── Cart Impersonation Modal ── */}
      {cartModal && (
        <div onClick={() => setCartModal(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:24, width:'100%', maxWidth:560, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, color:'var(--sedra-forest)', display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="ShoppingCart" size={16} /> {ar ? 'سلة العميل' : 'Customer Cart'}</h3>
                <div style={{ fontSize:12, color:'#888', marginTop:2 }}>{cartModal.label}</div>
              </div>
              <button onClick={() => setCartModal(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'var(--sedra-ink-muted)', display:'flex' }}><Icon name="X" size={20} /></button>
            </div>

            {cartMsg && <div style={{ background: cartMsg.startsWith('OK:') ? '#f0fdf4' : 'var(--status-danger-bg)', border:'1px solid ' + (cartMsg.startsWith('OK:') ? 'var(--status-success-bg)' : 'var(--status-danger-bg)'), borderRadius:7, padding:'8px 12px', marginBottom:12, fontSize:13, color: cartMsg.startsWith('OK:') ? 'var(--status-success)' : 'var(--status-danger)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name={cartMsg.startsWith('OK:') ? 'CheckCircle2' : 'XCircle'} size={14} /> {cartMsg.replace(/^(OK:|ERR:)/, '')}
            </div>}

            {cartLoading ? (
              <div style={{ color:'#aaa', textAlign:'center', padding:32 }}>…</div>
            ) : (
              <>
                {/* Add by SKU */}
                <div style={{ display:'flex', gap:8, marginBottom:14 }}>
                  <input value={cartAddSku} onChange={e => setCartAddSku(e.target.value.toUpperCase())}
                    onKeyDown={e => e.key === 'Enter' && cartAddBySkuModal()}
                    placeholder={ar ? 'كود المنتج (SKU)…' : 'Product SKU…'}
                    style={{ flex:1, padding:'8px 12px', borderRadius:7, border:'1.5px solid var(--sedra-line)', fontSize:13, fontFamily:'monospace' }} />
                  <button onClick={cartAddBySkuModal}
                    style={{ padding:'8px 16px', borderRadius:7, background:'var(--sedra-olive)', color:'#fff', border:'none', cursor:'pointer', fontWeight:700, fontSize:13 }}>
                    {ar ? '+ أضف' : '+ Add'}
                  </button>
                </div>

                {/* Cart items */}
                {cartItems.length === 0 ? (
                  <div style={{ color:'#bbb', textAlign:'center', padding:24, fontSize:14 }}>{ar ? 'السلة فارغة' : 'Cart is empty'}</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:14 }}>
                    {cartItems.map((item, idx) => (
                      <div key={item._key || idx} style={{ display:'flex', alignItems:'center', gap:10, background:'#f9f7f4', borderRadius:8, padding:'9px 12px' }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontWeight:600, fontSize:13, color:'var(--sedra-forest)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name_en}</div>
                          <div style={{ fontSize:11, color:'#aaa', fontFamily:'monospace' }}>{item.sku}</div>
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <button onClick={() => setCartItems(prev => prev.map((i,j) => j===idx ? {...i, qty: Math.max(i.min_qty||1, i.qty-1)} : i))} style={{ width:24, height:24, borderRadius:'50%', border:'1px solid var(--sedra-line)', background:'#fff', cursor:'pointer', fontSize:14 }}>−</button>
                          <span style={{ minWidth:28, textAlign:'center', fontWeight:700, fontSize:14 }}>{item.qty}</span>
                          <button onClick={() => setCartItems(prev => prev.map((i,j) => j===idx ? {...i, qty: i.qty+1} : i))} style={{ width:24, height:24, borderRadius:'50%', border:'1px solid var(--sedra-line)', background:'#fff', cursor:'pointer', fontSize:14 }}>+</button>
                        </div>
                        <button onClick={() => setCartItems(prev => prev.filter((_,j) => j !== idx))}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'var(--status-danger)', padding:'0 4px', display: 'flex' }}><Icon name="Trash2" size={15} /></button>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={() => setCartModal(null)} style={{ padding:'9px 18px', borderRadius:8, background:'var(--sedra-cream)', color:'#666', border:'1.5px solid var(--sedra-line)', cursor:'pointer', fontWeight:600, fontSize:13 }}>
                    {ar ? 'إلغاء' : 'Cancel'}
                  </button>
                  <button onClick={saveCartModal} disabled={cartSaving}
                    style={{ padding:'9px 22px', borderRadius:8, background: cartSaving ? '#ccc' : 'var(--sedra-olive)', color:'#fff', border:'none', cursor: cartSaving ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {!cartSaving && <Icon name="Wallet" size={13} />} {cartSaving ? '…' : (ar ? 'حفظ السلة' : 'Save Cart')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HERO BANNER — Ken Burns Carousel ────────────────────────────────────────

// ─── CATALOGUE VIEW ───────────────────────────────────────────────────────────

// Super-admin detector for "View as customer" (?admin=1). The session
// persisted by AdminLogin includes role/name/email; anything less than an
// unexpired super_admin session (sales/warehouse/manager/vendor, or a dead
// one) never qualifies — impersonation exposes the customer's pricing, orders
// and cart. Read once at mount via useMemo (the URL-sync effect never touches
// the admin param).
function readImpersonation() {
  try {
    if (new URLSearchParams(window.location.search).get('admin') !== '1') return null;
    return isSuperAdminSession() ? readSession() : null;
  } catch { return null; }
}

function CatalogueView() {
  const { token: _routeToken } = useParams();
  const impersonation = useMemo(readImpersonation, []);
  const [type, setType] = useState('noprice');
  const [tokenChecked, setTokenChecked] = useState(!_routeToken);
  const [tokenInvalid, setTokenInvalid] = useState(false);
  useEffect(() => {
    if (!_routeToken) { setType('noprice'); setTokenChecked(true); return; }
    fetch(`${SUPABASE_URL}/rest/v1/catalogue_tokens?token=eq.${encodeURIComponent(_routeToken)}&select=type,active,expires_at`, {
      headers: { apikey: SUPABASE_KEY, Accept: 'application/json' }
    }).then(r => r.json()).then(rows => {
      if (rows && rows.length > 0 && rows[0].active && (!rows[0].expires_at || new Date(rows[0].expires_at) > new Date())) {
        setType(rows[0].type);
      } else { setTokenInvalid(true); }
      setTokenChecked(true);
    }).catch(() => { setTokenInvalid(true); setTokenChecked(true); });
  }, [_routeToken]);
  const isMobile = useIsMobile();
  const [products, setProducts] = useState([]);
  // ── Cart — persisted to localStorage via useCart hook ─────────────────────
  // Must be called here (before early returns) to satisfy Rules of Hooks.
  // showVat is derived from `type` which is already in scope as state.
  const {
    cart,
    addToCart: _cartAdd,
    updateQty,
    clearCart,
    cartTotal,
    cartCount,
    getPrice: cartGetPrice,
    isSynced,
    // While impersonating, the cart is NOT bound to the customer's token: the
    // customer's server-side cart must never be overwritten by an admin demo
    // ("local wins" restore + debounced save_cart + clearCart after a test
    // order would all clobber it). The admin gets a local guest cart instead.
  } = useCart(VAT_RATE, type === 'vat', impersonation ? null : (_routeToken || null));

  // ── Phase 7: Retailer profile linked to this token ───────────────────────
  // Returns null for noprice (guest) tokens or pre-Phase-7 tokens.
  const { data: retailerProfile, isError: profileError } = useTokenProfile(_routeToken || null);

  // ── Phase 8: Variant selected in the product detail modal ────────────────
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('sedra_lang') || 'ar');
  useEffect(() => { localStorage.setItem('sedra_lang', lang); }, [lang]);
  const [category, setCategory] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('category') || 'all'; } catch { return 'all'; }
  });
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' | 'oldest'
  const [view, setView] = useState('catalogue'); // catalogue | cart | confirm | myorders
  // Pass token directly — do NOT gate on retailerProfile.
  // get_my_orders validates the token itself and returns [] for unlinked tokens.
  // Gating caused "No orders yet" flicker while retailerProfile was still loading.
  const { data: myOrders = [], isLoading: myOrdersLoading } = useMyOrders(_routeToken || null);
  const { details: orderDetails, fetchDetail } = useOrderDetail(_routeToken || null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  // ── PDF Quote — window.print() approach ──────────────────────────────────
  // Uses the browser's native print engine for 100% correct Arabic/RTL rendering.
  // html2canvas dropped: it does not support Complex Text Layout (CTL/Arabic shaping).
  const [quoteGenerating, setQuoteGenerating] = useState(false);
  // The printable sheet and the Excel workbook describe the SAME document in
  // two renderers, so resolve their shared payload once. Defaults to the live
  // cart (the price-quote buttons); pass opts to describe a PAST ORDER instead.
  const sheetPayload = (opts) => {
    const src      = opts?.items || cart;
    const priceOf  = opts?.priceOf || cartGetPrice;
    const totalVal = opts?.total != null ? opts.total : cartTotal;
    return {
      lang,
      kind:   opts?.kind || 'quote',
      refId:  opts?.refId || `QT-${Date.now()}`,
      dateMs: opts?.dateMs,
      party:  retailerProfile ? {
        company_name: retailerProfile.company_name,
        contact_name: retailerProfile.contact_name,
        phone:        retailerProfile.phone,
      } : null,
      items: src.map(i => ({ ...i, price: i.price == null ? null : priceOf(i) })),
      total: showPrice ? totalVal : null,
      showPrice,
      showVat,
    };
  };

  // Build and print the sheet. Pictures are compressed to embedded thumbnails
  // by invoiceSheet.js, so the PDF stays shareable.
  const generateQuote = async (opts) => {
    const payload = sheetPayload(opts);
    if (quoteGenerating || payload.items.length === 0) return;
    setQuoteGenerating(true);
    try {
      const ok = await openInvoiceSheet({ withPictures: true, ...payload });
      if (!ok) alert(lang === 'ar' ? 'يرجى السماح بالنوافذ المنبثقة' : 'Please allow popups for this site');
    } finally {
      setQuoteGenerating(false);
    }
  };

  // ── Excel export ─────────────────────────────────────────────────────────
  // Same document, spreadsheet renderer. Buyers asked for this because they
  // re-quantify lines and paste them into their own purchasing systems — work a
  // PDF forces them to retype. Its own busy flag so this button's spinner does
  // not freeze the PDF button beside it, and vice versa.
  const [excelGenerating, setExcelGenerating] = useState(false);
  const downloadExcel = async (opts) => {
    const payload = sheetPayload(opts);
    if (excelGenerating || payload.items.length === 0) return;
    setExcelGenerating(true);
    try {
      await downloadOrderWorkbook({ ...payload, withPictures: true });
    } catch {
      // The ~900 KB ExcelJS chunk is fetched on first click, so an offline or
      // flaky connection surfaces here rather than at page load.
      alert(lang === 'ar'
        ? 'تعذّر إنشاء ملف Excel. تحقق من اتصالك وحاول مرة أخرى.'
        : 'Could not create the Excel file. Check your connection and try again.');
    } finally {
      setExcelGenerating(false);
    }
  };

  // Export a PAST order as the same printable sheet, with product pictures.
  // Order items are stored without images (placeOrder snapshots only
  // id/sku/name/qty/price), so covers are resolved live from the catalogue by
  // product id — the same approach SharedOrderModal uses. Prices come from the
  // snapshot, so the sheet shows what was actually charged, not today's price.
  const orderExportOpts = (ord) => {
    const rows = orderDetails[ord.id]?.items;
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const items = rows.map(it => ({
      ...it,
      image: it.image || products.find(p => p.id === it.id)?.image || '',
    }));
    return {
      items,
      total: ord.total,
      refId: ord.id,
      dateMs: ord.created_at ? new Date(ord.created_at).getTime() : undefined,
      priceOf: (i) => i.price ?? 0,
      kind: 'order',
    };
  };

  const exportOrderSheet = (ord) => {
    const opts = orderExportOpts(ord);
    if (opts) generateQuote(opts);
  };

  const exportOrderExcel = (ord) => {
    const opts = orderExportOpts(ord);
    if (opts) downloadExcel(opts);
  };

  // ── OTP Gate for My Orders ───────────────────────────────────────────────
  // Cart + checkout are 100% unaffected — OTP only intercepts the history view.
  const [otpVerified,   setOtpVerified]   = useState(false); // session-only, resets on refresh
  // Staff impersonating a customer can't receive the OTP (it goes to the
  // CUSTOMER's email/phone), which made order history unreachable while
  // viewing as them. Skip the gate for staff — but only after the SERVER
  // confirms a super-admin JWT: `impersonation` is read from localStorage,
  // which the client controls, so trusting it alone would let anyone holding
  // a catalogue token skip the gate by editing storage.
  const [impersonationVerified, setImpersonationVerified] = useState(false);
  useEffect(() => {
    if (!impersonation) { setImpersonationVerified(false); return; }
    let cancelled = false;
    supaFetch('POST', 'rpc/is_super_admin', {}, '', { Prefer: 'return=representation' })
      .then(({ data }) => {
        // PostgREST returns a scalar for a boolean RPC; tolerate array shape.
        const ok = data === true || data?.[0] === true || data?.is_super_admin === true;
        if (!cancelled && ok) setImpersonationVerified(true);
      })
      .catch(() => {}); // not staff / expired → gate stays on, as it should
    return () => { cancelled = true; };
  }, [impersonation]);
  const [otpModal,      setOtpModal]      = useState(false);
  const [otpCode,       setOtpCode]       = useState('');
  const [otpSending,    setOtpSending]    = useState(false);
  const [otpVerifying,  setOtpVerifying]  = useState(false);
  const [otpSent,       setOtpSent]       = useState(false);
  const [otpMasked,     setOtpMasked]     = useState({ email: null, phone: null });
  const [otpError,      setOtpError]      = useState('');

  const sendOtp = async () => {
    if (!_routeToken || otpSending) return;
    setOtpSending(true); setOtpError('');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_KEY },
        body: JSON.stringify({ p_token: _routeToken }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to send code');
      setOtpMasked({ email: json.masked_email, phone: json.masked_phone });
      setOtpSent(true);
    } catch (e) {
      setOtpError(lang === 'ar' ? 'تعذّر إرسال الرمز: ' + e.message : 'Could not send code: ' + e.message);
    }
    setOtpSending(false);
  };

  const verifyOtp = async () => {
    if (!_routeToken || otpCode.length !== 4 || otpVerifying) return;
    setOtpVerifying(true); setOtpError('');
    try {
      const { data } = await supaFetch('POST', 'rpc/verify_otp', { p_token: _routeToken, p_code: otpCode }, '', { 'Prefer': 'return=representation' });
      const valid = Array.isArray(data) ? data[0] : data;
      if (valid === true || valid === 'true') {
        setOtpVerified(true); setOtpModal(false);
        setOtpCode(''); setOtpSent(false); setOtpError('');
        setView('myorders');
      } else {
        setOtpError(lang === 'ar' ? 'رمز غير صحيح أو منتهي الصلاحية' : 'Incorrect or expired code');
      }
    } catch (e) {
      setOtpError(lang === 'ar' ? 'حدث خطأ: ' + e.message : 'Error: ' + e.message);
    }
    setOtpVerifying(false);
  };

  // ── Quick Add ────────────────────────────────────────────────────────────
  const [showQuickAdd,   setShowQuickAdd]   = useState(false);
  const [quickAddText,   setQuickAddText]   = useState('');
  const [quickAddResults, setQuickAddResults] = useState(null); // null | parsed[]

  const parseQuickAdd = (text) => {
    const lines = text.split(/\n/).map(l => l.trim()).filter(Boolean);
    return lines.map(line => {
      // Formats: "SKU x 12", "SKU×12", "SKU 12", "SKU"
      const m = line.match(/^([^\s]+)\s*[xX×*]\s*(\d+)$/)
             || line.match(/^([^\s]+)\s+(\d+)$/)
             || [null, line, null];
      const sku = (m[1] || '').trim().toUpperCase();
      const requestedQty = m[2] ? parseInt(m[2]) : 1;
      if (!sku) return { line, status: 'invalid' };
      const product = products.find(p => p.sku?.toUpperCase() === sku && p.is_active !== false);
      if (!product) return { line, sku, requestedQty, status: 'notfound' };
      const finalQty = Math.max(product.min_qty || 1, requestedQty);
      return { line, sku, product, requestedQty, qty: finalQty, status: finalQty > requestedQty ? 'adjusted' : 'ok' };
    });
  };

  const applyQuickAdd = (parsed) => {
    const toAdd = (parsed || quickAddResults || []).filter(r => r.status === 'ok' || r.status === 'adjusted');
    toAdd.forEach(r => addToCart(r.product, r.qty));
    if (toAdd.length) {
      setToast(lang === 'ar' ? `تمت إضافة ${toAdd.length} منتج` : `${toAdd.length} products added to cart`);
      setTimeout(() => setToast(''), 3000);
      setShowQuickAdd(false); setQuickAddText(''); setQuickAddResults(null);
    }
  };
  const [orderForm, setOrderForm] = useState({ business_name: '', contact_name: '', phone: '', whatsapp: '', email: '', address: '', notes: '', payment_method: 'instapay' });
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [flashId, setFlashId] = useState(null);   // card that was just added
  const [toast, setToast] = useState('');          // brief top toast message
  const [detailLoading, setDetailLoading] = useState(false); // spinner inside modal
  const [modalQty, setModalQty] = useState(1);         // qty in product detail modal
  const [visibleCount, setVisibleCount] = useState(24);
  // Fallback banners — empty by default; hero shows clean gradient until real
  // images are loaded from the site_banners DB table.
  // TO ADD BANNERS: go to Admin → Banners, paste a public image URL and Save.
  // Upload your brand photos to Supabase Storage (product-images bucket, public)
  // then add the signed/public URL here or via the admin panel.
  const FALLBACK_BANNERS = [];
  const [banners, setBanners] = useState(FALLBACK_BANNERS);
  const [bannerIdx, setBannerIdx] = useState(0);
  // Track whether at least one banner photo has loaded — controls the dark overlay
  const [anyBannerLoaded, setAnyBannerLoaded] = useState(false);
  // Footer info — loaded from the same DB settings the admin configures
  const [footerInfo, setFooterInfo] = useState({ phone: '', email: '', address: '', instagram: '', whatsapp: '' });
  // Stock-tracking flag (public via anon policy) — shared cached read so the
  // storefront hides stock states when off and picks up admin toggles live.
  const stockTracking = useStockTracking();
  // Merchandising: sort own products ahead of vendor products (default on).
  const ownFirst = useOwnProductsFirst();
  const categoryTabsRef   = useRef(null);  // for scroll-into-view on deep-link
  const sentinelObserver  = useRef(null);  // infinite scroll IntersectionObserver instance
  const _didAutoOpen      = useRef(false); // guard: only auto-open once per load
  const _isFirstMount     = useRef(true);  // guard: skip page reset on initial render
  // Capture ?product= IMMEDIATELY at render time before any URL-sync effect can strip it
  const _initialProductSku = useRef((() => {
    try { return new URLSearchParams(window.location.search).get('product') || null; } catch { return null; }
  })());
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // ── Shared order draft (?draft=…) — a manager opening a buyer's shared link ──
  const _initialDraft = useRef((() => {
    try { return decodeOrderDraft(new URLSearchParams(window.location.search).get('draft')); } catch { return null; }
  })());
  const [sharedDraft, setSharedDraft] = useState(_initialDraft.current);

  // ── Impersonation audit ──
  // One row per impersonated page load (the banner itself renders off
  // `impersonation`, declared at the top of the component). Waits for the
  // token profile only while it can still resolve — a failed profile query or
  // a tokenless route must not suppress the audit record. A failed insert
  // re-arms the flag so a later effect run can retry, and leaves a console
  // trace instead of vanishing. admin_user_id also defaults to auth.uid()
  // server-side, and the RLS with-check rejects a mismatched uuid.
  const _impLogged = useRef(false);
  useEffect(() => {
    if (!impersonation || _impLogged.current) return;
    if (_routeToken && retailerProfile === undefined && !profileError) return; // profile still loading
    _impLogged.current = true;
    supaFetch('POST', 'admin_impersonation_log', {
      admin_user_id: impersonation.user_id || null,
      admin_email: impersonation.email || null,
      admin_name:  impersonation.name  || null,
      admin_role:  impersonation.role  || null,
      token: _routeToken || null,
      retailer_application_id: retailerProfile?.id || null,
      account_label: retailerProfile?.company_name || null,
    }, '', { Prefer: 'return=minimal' }).catch((e) => {
      _impLogged.current = false;
      console.warn('[impersonation-audit] failed to record session:', e?.message);
    });
  }, [impersonation, retailerProfile, profileError, _routeToken]);

  // ── Phase 3: lead-capture apply modal ────────────────────────────────────────
  const [showApplyModal, setShowApplyModal]   = useState(false);
  const [applyForm, setApplyForm]             = useState({ company_name: '', contact_name: '', email: '', phone: '', tax_id: '' });
  const [applySubmitting, setApplySubmitting] = useState(false);
  const [applySuccess, setApplySuccess]       = useState(false);
  const [applyError, setApplyError]           = useState('');

  const submitApplication = async (e) => {
    e.preventDefault();
    setApplySubmitting(true); setApplyError('');
    try {
      const applied_at = new Date().toISOString();
      await supaFetch('POST', 'retailer_applications', {
        company_name:       applyForm.company_name.trim(),
        contact_name:       applyForm.contact_name.trim(),
        email:              applyForm.email.trim().toLowerCase(),
        phone:              applyForm.phone.trim(),
        tax_id:             applyForm.tax_id.trim() || null,
        preferred_language: lang,
      }, '', { 'Prefer': 'return=minimal' });

      // Notify admin — best-effort, never blocks the success screen
      fetch(`${SUPABASE_URL}/functions/v1/notify-new-application`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_KEY },
        body:    JSON.stringify({
          company_name: applyForm.company_name.trim(),
          contact_name: applyForm.contact_name.trim(),
          email:        applyForm.email.trim().toLowerCase(),
          phone:        applyForm.phone.trim(),
          tax_id:       applyForm.tax_id.trim() || null,
          applied_at,
        }),
      }).catch(() => {}); // fire-and-forget

      setApplySuccess(true);
      setApplyForm({ company_name: '', contact_name: '', email: '', phone: '', tax_id: '' });
    } catch (err) {
      setApplyError(lang === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.');
    } finally {
      setApplySubmitting(false);
    }
  };

  const BATCH_SIZE = 24; // items rendered per scroll batch

  // ── Phase 7: Pre-fill checkout form from retailer profile ────────────────
  // Runs once when the profile arrives. Does not overwrite if user already typed.
  useEffect(() => {
    if (!retailerProfile) return;
    setOrderForm(prev => ({
      ...prev,
      business_name: prev.business_name || retailerProfile.company_name || '',
      contact_name:  prev.contact_name  || retailerProfile.contact_name  || '',
      email:         prev.email         || retailerProfile.email          || '',
      phone:         prev.phone         || retailerProfile.phone          || '',
    }));
  }, [retailerProfile]);

  const t = T[lang];

  // Fonts load once, globally, from src/styles/tokens/fonts.css (imported in
  // src/index.js) — no per-component injection needed here anymore.
  // Phase 0: Products are fetched via SECURITY DEFINER RPC — price is NULLed at the
  // database level for noprice tokens. Direct REST access to /rest/v1/products is
  // blocked for anon by RLS (no anon_read policy). This eliminates the price leak at
  // the network layer, not just the UI layer.
  // PostgREST caps a single response at 1,000 rows and silently ignores Range
  // headers on POST-style RPC calls, so the catalogue must be paged through the
  // GET form of the RPC until a page comes back short. The RPC treats a missing
  // client_token the same as NULL (public no-price catalogue).
  useEffect(() => {
    if (!tokenChecked) return;
    const PAGE = 1000;
    const query = _routeToken ? `?client_token=${encodeURIComponent(_routeToken)}` : '';
    (async () => {
      const all = [];
      for (let from = 0; ; from += PAGE) {
        const { data: page } = await supaFetch('GET', 'rpc/get_secure_catalogue', null, query, {
          'Range-Unit': 'items',
          'Range': `${from}-${from + PAGE - 1}`,
        });
        if (Array.isArray(page)) all.push(...page);
        if (!Array.isArray(page) || page.length < PAGE) break;
      }
      setProducts(all);
      setLoading(false);
    })().catch(() => setLoading(false));
  }, [tokenChecked]);
  // Fetch active banners for the storefront hero carousel
  useEffect(() => {
    supaFetch('GET', 'site_banners', null, '?active=eq.true&order=sort_order.asc')
      .then(({ data }) => { if (data && data.length) setBanners(data); })
      .catch(() => {});
  }, []);
  // Load company info for the footer from the same settings the admin configures
  useEffect(() => {
    Promise.all([
      db.getSetting('company_phone'),
      db.getSetting('company_email'),
      db.getSetting('company_address'),
      db.getSetting('footer_instagram'),
      db.getSetting('footer_whatsapp'),
    ]).then(([phone, email, address, instagram, whatsapp]) => {
      setFooterInfo({
        phone:     phone     || '',
        email:     email     || 'info@sedrahome.com',
        address:   address   || 'https://maps.app.goo.gl/4Rvdpmqi5RnUraF46',
        instagram: instagram || 'https://www.instagram.com/sedrahome',
        whatsapp:  whatsapp  || 'https://api.whatsapp.com/send?phone=201204114211',
      });
    }).catch(() => {});
  }, []);
  // Auto-advance banner carousel
  useEffect(() => {
    if (banners.length < 2) return;
    const id = setInterval(() => setBannerIdx(i => (i + 1) % banners.length), 5000);
    return () => clearInterval(id);
  }, [banners.length]);
  // Transparent-to-solid header on scroll
  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Category change → reset + scroll to top (user switched context).
  useEffect(() => {
    if (_isFirstMount.current) { _isFirstMount.current = false; return; }
    setVisibleCount(BATCH_SIZE);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [category]);

  // Search change → reset visible count only. NO scroll.
  // Scrolling to top on every keystroke hijacks the user's position and makes
  // the search feel broken even though the filter is working correctly.
  useEffect(() => {
    if (_isFirstMount.current) return;
    setVisibleCount(BATCH_SIZE);
  }, [search]);

  // ── URL sync: silently keep address bar in step with state ──────────────────
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (category && category !== 'all') params.set('category', category); else params.delete('category');
      if (selectedProduct) params.set('product', selectedProduct.sku); else params.delete('product');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
    } catch {}
  }, [category, selectedProduct]);

  // ── Infinite scroll: callback ref — observer attaches/detaches with the spinner element ──
  // The spinner is conditionally rendered only when visibleCount < filtered.length,
  // so when all items are shown the element unmounts and the observer auto-disconnects.
  const sentinelCallback = useCallback((node) => {
    if (sentinelObserver.current) {
      sentinelObserver.current.disconnect();
      sentinelObserver.current = null;
    }
    if (!node) return;
    sentinelObserver.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisibleCount(c => c + BATCH_SIZE);
    }, { rootMargin: '200px' });
    sentinelObserver.current.observe(node);
  }, []); // stable — setVisibleCount and BATCH_SIZE never change

  // ── Scroll the active category tab into view on deep-link arrival ───────────
  useEffect(() => {
    if (!categoryTabsRef.current) return;
    const el = categoryTabsRef.current.querySelector('[data-active="true"]');
    if (el) el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [category]);

  // ── Auto-open product from ?product=SKU once the catalogue has loaded ───────
  // Root-cause note: the URL-sync effect fires on mount with selectedProduct=null
  // and calls params.delete('product'), stripping the param BEFORE products load.
  // Fix: _initialProductSku captures the SKU synchronously at render time (line
  // above) so it's immune to the URL-sync wipe.
  useEffect(() => {
    if (!products || products.length === 0) return; // wait for data
    if (_didAutoOpen.current) return;               // only once per page load
    const urlProductSku = _initialProductSku.current;
    if (!urlProductSku) return;
    _didAutoOpen.current = true; // claim the slot before the timeout fires
    // Give the React tree 500ms to finish its first paint before mounting the modal
    setTimeout(() => {
      try {
        // Case-insensitive search in the GLOBAL products array (not filtered list)
        const matchIdx = products.findIndex(
          p => p.sku?.toLowerCase() === urlProductSku.toLowerCase()
        );
        if (matchIdx !== -1) {
          const match = products[matchIdx];
          // DOM sync fix: advance visibleCount so the product is rendered in the
          // grid behind the modal. When the user dismisses the modal they land in
          // context, not staring at only the first 24 items.
          // products[] and filtered[] share the same default sort (created_at desc)
          // so matchIdx is a reliable proxy for the product's position in the grid.
          if (matchIdx >= BATCH_SIZE) {
            setVisibleCount(matchIdx + BATCH_SIZE);
          }
          // If URL also has ?category=, set it first so filters are consistent
          const urlCat = (() => {
            try { return new URLSearchParams(window.location.search).get('category'); } catch { return null; }
          })();
          if (urlCat && urlCat !== 'all') setCategory(urlCat);
          openProduct(match);
        } else {
          console.warn('[Sedra] SKU not found in product list:', urlProductSku);
          setToast(lang === 'ar' ? 'المنتج لم يعد متاحاً' : 'Product no longer available');
          setTimeout(() => setToast(''), 3200);
        }
      } catch (e) {
        console.error('[Sedra] auto-open error:', e);
      }
    }, 500);
  }, [products]); // intentional: only re-run when product list loads

  if (!tokenChecked) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', color:'#888', fontSize:16 }}>Loading catalogue…</div>;
  if (tokenInvalid) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif', flexDirection:'column', gap:16 }}>
      <div style={{ display:'flex', justifyContent:'center' }}><Icon name="Lock" size={56} /></div>
      <div style={{ fontSize:20, fontWeight:700, color:'var(--sedra-forest-soft)' }}>Invalid or expired link</div>
      <div style={{ color:'#aaa', fontSize:14 }}>Please ask Sedra Home for a valid catalogue link.</div>
    </div>
  );
  const showPrice = type !== 'noprice';
  const showVat = type === 'vat';
  // Neutral beige placeholder — shown when an image URL is missing or returns 404
  const IMG_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23ECEAE4'/%3E%3C/svg%3E";
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const NEW_DAYS = 30; // products added within this many days show the "New" badge
  const NEW_CUTOFF = new Date(Date.now() - NEW_DAYS * 24 * 60 * 60 * 1000);

  const usedCatIds = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
  // Custom categories: any category string in the DB that isn't in CATEGORY_TAXONOMY
  // catLabel() already handles these gracefully — returns the raw string as-is
  const dynamicStorefrontCats = usedCatIds.filter(c => !CATEGORY_TAXONOMY.find(cx => cx.id === c));
  // 'new' is a virtual category — shown only when recent products exist
  const hasNewArrivals = products.some(p => p.created_at && new Date(p.created_at) >= NEW_CUTOFF);
  const categories = [
    'all',
    ...(hasNewArrivals ? ['new'] : []),
    ...CATEGORY_TAXONOMY.map(c => c.id).filter(id => usedCatIds.includes(id)),
    ...dynamicStorefrontCats,
  ];
  // Arabic display names for custom categories — keyed by category (EN) value
  const catArMap = Object.fromEntries(
    products.filter(p => p.category && p.category_ar).map(p => [p.category, p.category_ar])
  );
  const filtered = products
    .filter(p => {
      const matchCat =
        category === 'all' ? true :
        category === 'new' ? (p.created_at && new Date(p.created_at) >= NEW_CUTOFF) :
        p.category === category;
      const matchSearch =
        p.name_en?.toLowerCase().includes(search.toLowerCase()) ||
        p.name_ar?.includes(search) ||
        p.sku?.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      // Own (first-party) products first when enabled — a tier ON TOP of the
      // date sort, which still applies as the tiebreaker within each group.
      // "Own" = no vendor name, the same signal the card uses to show
      // "Sold by … via Sedra Home" (the catalogue RPC returns vendor_name_*,
      // not necessarily vendor_id, so key on the field the UI already trusts).
      if (ownFirst) {
        const aOwn = !(a.vendor_name_en || a.vendor_name_ar);
        const bOwn = !(b.vendor_name_en || b.vendor_name_ar);
        if (aOwn !== bOwn) return aOwn ? -1 : 1;
      }
      return sortOrder === 'oldest'
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    });

  const displayed = filtered.slice(0, visibleCount);

  // Thin wrapper: delegates cart state to the hook; adds visual feedback locally.
  // Phase 8: accepts optional `variant` — passed through to useCart.addToCart.
  const addToCart = (product, customQty, variant = null) => {
    _cartAdd(product, customQty, variant);
    setFlashId(product.id);
    setTimeout(() => setFlashId(null), 900);
    setToast(lang === 'ar' ? 'تمت الإضافة إلى السلة' : 'Added to cart');
    setTimeout(() => setToast(''), 2400);
  };

  // ── Share this cart with a manager (self-contained ?draft= link) ──
  const shareOrder = async () => {
    if (cart.length === 0) return;
    const lines = cart.map(i => ({
      id: i.id, sku: i.sku, name_en: i.name_en, name_ar: i.name_ar, qty: i.qty,
      price: showPrice && i.price != null ? cartGetPrice(i) : null,
    }));
    const enc = encodeOrderDraft({ type, note: orderForm.notes || '', lines });
    const url = window.location.origin + window.location.pathname + '?draft=' + enc;
    const shareData = {
      title: lang === 'ar' ? 'طلب من سدرة هوم' : 'Sedra Home order',
      text:  lang === 'ar' ? 'مراجعة هذا الطلب' : 'Review this order',
      url,
    };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else { await navigator.clipboard.writeText(url); setToast(lang === 'ar' ? 'تم نسخ رابط المشاركة' : 'Share link copied'); setTimeout(() => setToast(''), 2600); }
    } catch { /* user dismissed the share sheet — no-op */ }
  };

  // ── Manager opened a shared draft → load its lines into the cart to place it ──
  const placeSharedOrder = () => {
    if (!sharedDraft) return;
    clearCart();
    let missing = 0;
    sharedDraft.lines.forEach(l => {
      const p = products.find(x => x.id === l.id);
      if (p) { _cartAdd(p, l.qty); } else { missing += 1; }
    });
    setSharedDraft(null);
    try { const u = new URL(window.location.href); u.searchParams.delete('draft'); window.history.replaceState({}, '', u.pathname + u.search); } catch { /* ignore */ }
    if (missing > 0) { setToast(lang === 'ar' ? `${missing} صنف لم يعد متاحاً` : `${missing} item(s) no longer available`); setTimeout(() => setToast(''), 3200); }
  };

  // Open product modal — lazily fetch full details (images array, description) on first open.
  // For noprice tokens, the detail fetch also omits price so it never appears in the
  // network payload even when the modal is opened.
  const openProduct = async (p) => {
    setSelectedProduct(p);
    setModalQty(p.min_qty || 1);
    setImgIndex(0);
    setSelectedVariant(null);  // Phase 8: reset variant selection
    if (!p._fullLoaded) {
      setDetailLoading(true);
      try {
        // Phase 0: detail fetch via RPC — DB NULLs price for noprice tokens
        const { data: rows } = await supaFetch('POST', 'rpc/get_secure_product',
          { client_token: _routeToken || null, product_id: p.id }, '');
        if (rows && rows[0]) {
          const full = { carton_qty: p.carton_qty, ...rows[0], _fullLoaded: true };
          setSelectedProduct(full);
          setProducts(prev => prev.map(x => x.id === p.id ? { ...x, images: full.images, image_gallery: full.image_gallery, category_ar: full.category_ar, description: full.description, description_ar: full.description_ar, name_ar: full.name_ar, _fullLoaded: true } : x));
        }
      } catch {}
      setDetailLoading(false);
    }
  };

  // updateQty, cartTotal, cartCount come from useCart hook above.
  // getPrice is a local display helper — kept here for placeOrder payload + cart render.
  // For noprice tokens, p.price is null (DB-level security), so getPrice returns 0
  // regardless of showPrice — keeping arithmetic clean and never leaking prices.
  const getPrice = (p) => {
    if (!showPrice || p.price === undefined || p.price === null) return 0;
    if (!showVat) return p.price;
    return Math.ceil(p.price * (1 + VAT_RATE) / 5) * 5;
  };

  const placeOrder = async () => {
    // For identified retailers profile provides contact/phone; guests must fill them.
    if (!retailerProfile && (!orderForm.contact_name || !orderForm.phone)) return;
    setSubmitting(true);
    const id = 'ORD-' + Date.now();
    const order = {
      id, catalogue_type: type,
      business_name: orderForm.business_name,
      contact_name: orderForm.contact_name,
      phone: orderForm.phone, whatsapp: orderForm.whatsapp,
      email: orderForm.email, address: orderForm.address,
      notes: orderForm.notes,
      payment_method: orderForm.payment_method || 'instapay',
      // For noprice orders, price/total are null — pricing is handled by the admin separately
      retailer_id: retailerProfile?.id || null,  // Phase 7: link order to retailer
      items: cart.map(i => ({
        id: i.id, sku: i.sku, name_en: i.name_en, name_ar: i.name_ar,
        qty: i.qty, price: type === 'noprice' ? null : cartGetPrice(i),
        variant_id: i.variant_id || null, variant_label: i.variant_label || null,
        // Marketplace: per-vendor attribution for settlement
        vendor_id: i.vendor_id || null, vendor_name: i.vendor_name || null,
      })),
      total: type === 'noprice' ? null : cartTotal, status: 'new',
      // Attribution hint only: the trg_stamp_placed_by_admin trigger derives
      // the authoritative value from the caller's JWT + staff_roles and
      // overwrites whatever the client sends (forgery- and omission-proof).
      placed_by_admin: impersonation
        ? (impersonation.email || impersonation.name || 'admin')
        : null,
      preferred_language: lang,
      created_at: new Date().toISOString()
    };
    try {
      await supaFetch('POST', 'rpc/submit_order', { order_data: order }, '', { 'Prefer': 'return=minimal' });

      // ── AUTO-CAPTURE CUSTOMER ──────────────────────────────────────────────
      // Upsert into customers table keyed by phone number.
      // Stores full contact details + tracks source, last order date, total spent.
      try {
        const { data: existing } = await supaFetch('GET', 'customers', null,
          `?phone=eq.${encodeURIComponent(orderForm.phone)}&limit=1`);
        const customerRecord = {
          business_name: orderForm.business_name || '',
          contact_name:  orderForm.contact_name  || '',
          phone:    orderForm.phone,
          whatsapp: orderForm.whatsapp || orderForm.phone,
          email:    orderForm.email    || '',
          address:  orderForm.address  || '',
          notes:    orderForm.notes    || '',
          source:       'order',
          last_order_id: id,
          last_order_at: order.created_at,
          // Link the CRM row to the account whose catalogue this order came
          // from, so "View as customer" on the Customers screen is an exact
          // lookup rather than an email/phone guess.
          retailer_application_id: retailerProfile?.id || null,
        };
        if (existing.length > 0) {
          await db.update('customers', existing[0].id, customerRecord);
        } else {
          await db.insert('customers', { ...customerRecord, id: uid(), created_at: order.created_at });
        }
      } catch {}  // non-fatal — order still goes through

      const summary = cart.map(i => `${i.sku} x${i.qty}`).join(', ');
      const webhookUrl = await db.getSetting('webhook_url');
      if (webhookUrl) {
        fetch(webhookUrl, { method: 'POST', body: JSON.stringify({ order_id: id, date: (order.created_at || '').slice(0, 10), business_name: orderForm.business_name, contact_name: orderForm.contact_name, phone: orderForm.phone, whatsapp: orderForm.whatsapp, email: orderForm.email, catalogue_type: type, items_summary: summary, total: cartTotal, notes: orderForm.notes }) }).catch(() => {});
      }
      // Notify admin via Resend + WhatsApp — replaces formsubmit.co (required activation, unreliable)
      fetch(`${SUPABASE_URL}/functions/v1/notify-new-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_KEY },
        body: JSON.stringify({
          order_id:      id,
          business_name: orderForm.business_name,
          contact_name:  orderForm.contact_name,
          phone:         orderForm.phone,
          total:         type === 'noprice' ? null : cartTotal,
          items_summary: summary,
          items_count:   cart.length,
          catalogue_type: type,
        }),
      }).catch(() => {}); // fire-and-forget — never blocks order confirmation
      posthog.capture('order_placed', {
        order_id: id,
        catalogue_type: type,
        payment_method: order.payment_method,
        product_count: cart.length,
        item_quantity: cart.reduce((sum, item) => sum + item.qty, 0),
        order_total: order.total,
        currency: 'EGP',
        has_retailer_profile: Boolean(retailerProfile),
      });
      setOrderId(id); clearCart(); setView('confirm');
    } catch (e) {
      posthog.capture('order_placement_failed', {
        catalogue_type: type,
        product_count: cart.length,
        has_retailer_profile: Boolean(retailerProfile),
      });
      posthog.captureException(e, { flow: 'order_placement' });
      alert('Error placing order: ' + e.message);
    }
    setSubmitting(false);
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', outline: 'none' };

  if (loading) return <LoadingScreen lang={lang} />;

  const typeLabel = type === 'vat' ? t.vatCatalogue : type === 'noprice' ? t.noprice : t.standard;

  return (
    <div className={impersonation ? 'sdra-imp-pad' : undefined} style={{ minHeight: '100vh', background: 'var(--sedra-cream)', fontFamily: lang === 'ar' ? "'Tajawal','DM Sans',sans-serif" : "'DM Sans','Tajawal',sans-serif", direction: dir, WebkitFontSmoothing: 'antialiased', color: 'var(--sedra-ink)', paddingTop: view === 'catalogue' ? 0 : (isMobile ? 60 : 72) }}>
      <style>{`
        @keyframes sdra-cartBounce { 0%,100%{transform:scale(1)} 35%{transform:scale(1.22)} 65%{transform:scale(0.92)} }
        @keyframes sdra-flashRing  { 0%{box-shadow:0 0 0 0 rgba(122,139,104,0.9)} 100%{box-shadow:0 0 0 12px rgba(122,139,104,0)} }
        @keyframes sdra-toastIn    { from{opacity:0;transform:translateX(-50%) translateY(-16px) scale(0.94)} to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)} }
        @keyframes sdra-addedCheck { 0%{opacity:0;transform:scale(0.6)} 50%{opacity:1;transform:scale(1.1)} 100%{opacity:1;transform:scale(1)} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .sdra-card { transition: transform 0.18s ease, box-shadow 0.18s ease !important; }
        .sdra-card:hover { transform: translateY(-4px) !important; box-shadow: 0 16px 40px rgba(74,82,64,0.16) !important; }
        .sdra-card:hover .sdra-img { transform: scale(1.07) !important; }
        .sdra-img { transition: transform 0.4s ease !important; }
        .sdra-addBtn { transition: background 0.15s, transform 0.12s !important; }
        .sdra-addBtn:hover { background: #3A4534 !important; }
        .sdra-addBtn:active { transform: scale(0.95) !important; }
        * { -webkit-font-smoothing: antialiased; }
        .sdra-qtyInput { -moz-appearance: textfield !important; }
        .sdra-qtyInput::-webkit-outer-spin-button,
        .sdra-qtyInput::-webkit-inner-spin-button { -webkit-appearance: none !important; margin: 0 !important; }
        @keyframes sedra-kenBurns { from { transform: scale(1.07); } to { transform: scale(1); } }
        @keyframes sedra-slideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes sedra-progressBar { from { width: 0%; } to { width: 100%; } }
        @keyframes sedra-checkDraw { to { stroke-dashoffset: 0; } }
        @keyframes sedra-circleIn { from { transform: scale(0.6); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .sedra-card-new { background:#fff; border-radius:16px; overflow:hidden; border:1px solid rgba(0,0,0,0.055); box-shadow:0 2px 8px rgba(0,0,0,0.04); cursor:pointer; transition: transform 0.28s cubic-bezier(0.22,1,0.36,1), box-shadow 0.28s cubic-bezier(0.22,1,0.36,1); }
        .sedra-card-new:hover { transform:translateY(-4px); box-shadow:0 14px 36px rgba(42,54,38,0.13); }
        .sedra-card-new:hover .sdra-img { transform: scale(1.07) !important; }
        .sedra-pill-add { transition: background 0.15s, transform 0.12s !important; }
        .sedra-pill-add:hover { background: var(--sedra-olive-deep) !important; }
        .sedra-pill-add:active { transform: scale(0.96) !important; }
        .sedra-cat-strip { display:flex; gap:4px; padding:5px; background:rgba(255,255,255,0.76); backdrop-filter:blur(14px); -webkit-backdrop-filter:blur(14px); border-radius:14px; border:1px solid rgba(255,255,255,0.65); box-shadow:0 2px 10px rgba(0,0,0,0.06); overflow-x:auto; scrollbar-width:none; }
        .sedra-cat-strip::-webkit-scrollbar { display:none; }
        .sedra-wa-bar { position:fixed; bottom:24px; right:24px; z-index:95; width:56px; height:56px; border-radius:50%; background:#25D366; display:flex; align-items:center; justify-content:center; cursor:pointer; box-shadow:0 4px 18px rgba(37,211,102,0.42); transition:transform 0.22s cubic-bezier(0.22,1,0.36,1),box-shadow 0.22s; }
        .sedra-wa-bar:hover { transform:scale(1.09); box-shadow:0 8px 26px rgba(37,211,102,0.52); }
      `}</style>

      {/* Admin impersonation banner — "View as customer".
          zIndex sits BELOW the cart/product/OTP modals (1000/2000/3000) so it
          never covers a modal's action buttons, and the companion style block
          pads the page bottom + lifts the WhatsApp FAB so nothing is occluded. */}
      {impersonation && (
        <>
          <style>{`
            .sdra-imp-pad { padding-bottom: 64px; }
            .sedra-wa-bar { bottom: 88px !important; }
          `}</style>
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 900, background: 'var(--sedra-forest)', color: '#fff', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap', fontSize: 13, fontWeight: 700, boxShadow: '0 -4px 16px rgba(0,0,0,0.25)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Icon name="Eye" size={15} />
              {lang === 'ar'
                ? `عرض كالعميل${retailerProfile?.company_name ? ': ' + retailerProfile.company_name : ''} — أنت ${impersonation.name || 'مشرف'}`
                : `Viewing as customer${retailerProfile?.company_name ? ': ' + retailerProfile.company_name : ''} — you are ${impersonation.name || 'admin'}`}
            </span>
            <button onClick={() => { window.location.href = '/admin'; }}
              style={{ background: 'var(--sedra-tan)', color: 'var(--sedra-forest)', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 800, cursor: 'pointer', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="ArrowLeft" size={13} /> {lang === 'ar' ? 'العودة للوحة التحكم' : 'Exit to admin'}
            </button>
          </div>
        </>
      )}

      {/* Shared order draft (manager opened a buyer's ?draft= link) */}
      {sharedDraft && (
        <SharedOrderModal
          draft={sharedDraft}
          products={products}
          lang={lang}
          isMobile={isMobile}
          onPlace={placeSharedOrder}
          onClose={() => { setSharedDraft(null); try { const u = new URL(window.location.href); u.searchParams.delete('draft'); window.history.replaceState({}, '', u.pathname + u.search); } catch { /* ignore */ } }}
        />
      )}

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'fixed', top: 82, left: '50%',
          background: 'var(--sedra-forest)', color: '#E8F0E2',
          padding: '12px 28px', borderRadius: 32,
          fontSize: 14, fontWeight: 700, zIndex: 9999,
          letterSpacing: 0.4, boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
          animation: 'sdra-toastIn 0.22s cubic-bezier(0.34,1.56,0.64,1) forwards',
          pointerEvents: 'none',
        }}>
          {toast}
        </div>
      )}

      {/* ── HEADER — transparent on hero, solid dark-green at 60px scroll ── */}
      <div style={{
        background: 'rgba(247,244,239,0.92)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 12px rgba(0,0,0,0.07)',
        position: 'sticky', top: 0, zIndex: 100,
        padding: isMobile ? '0 14px' : '0 28px',
      }}>
        {/* h-20 = 80px strict; alignItems:center keeps logo + controls vertically centred */}
        <div style={{ maxWidth: 1140, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: isMobile ? 60 : 72 }}>
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img
              src={LOGO_URL}
              alt="Sedra Home"
              style={{ height: isMobile ? 48 : 44, maxHeight: isMobile ? 48 : 44, width: 'auto', borderRadius: 10, objectFit: 'contain', flexShrink: 0 }}
            />
            {!isMobile && (
              <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--sedra-ink)', letterSpacing: '-0.02em' }}>Sedra Home</span>
            )}
          </div>
          {/* Right controls — slim & elegant */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <a href="/sell" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: isMobile ? '6px 10px' : '7px 14px', borderRadius: 999, background: 'var(--sedra-pistachio-deep)', color: 'var(--sedra-cream)', fontSize: isMobile ? 11 : 12.5, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              <Icon name="Store" size={isMobile ? 13 : 14} /> {lang === 'ar' ? 'بيع معنا' : 'Sell with us'}
            </a>
            <LangToggle lang={lang} setLang={setLang} variant="light" size={isMobile ? 'sm' : 'md'} />
            {/* Platform updates — badges only what this device hasn't read */}
            <WhatsNew lang={lang} isMobile={isMobile} />
            {/* Export Catalog — only for retailers with export permission */}
            {retailerProfile?.can_export_data && products.length > 0 && (
              <button
                onClick={() => {
                  // Stock column only when tracking is on — the toggle promises stock
                  // states are hidden from the storefront, and the export is one of them.
                  const headers = ['SKU', 'Name EN', 'Name AR', 'Category', 'Price (EGP)', 'Min Qty', 'Carton Qty', ...(stockTracking ? ['Stock'] : [])];
                  const rows = products.map(p => [
                    p.sku || '',
                    (p.name_en || '').replace(/"/g, '""'),
                    (p.name_ar || '').replace(/"/g, '""'),
                    (p.category || '').replace(/"/g, '""'),
                    showPrice && p.price != null ? (showVat ? Math.ceil(p.price * 1.14 / 5) * 5 : p.price) : '',
                    p.min_qty || 1,
                    p.carton_qty || 1,
                    ...(stockTracking ? [p.stock ?? ''] : []),
                  ]);
                  const csv = '﻿' + [headers, ...rows]
                    .map(r => r.map(c => `"${c}"`).join(','))
                    .join('\n');
                  const a = document.createElement('a');
                  a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                  a.download = `sedra_catalog_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                }}
                title={lang === 'ar' ? 'تصدير الكتالوج CSV' : 'Export Catalog CSV'}
                style={{
                  background: 'rgba(45,58,40,0.08)', border: '1.5px solid rgba(45,58,40,0.15)',
                  color: 'var(--sedra-forest)', padding: isMobile ? '8px 12px' : '9px 16px',
                  borderRadius: 100, cursor: 'pointer', fontSize: isMobile ? 13 : 13,
                  fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
                }}>
                <Icon name="Download" size={16} />{!isMobile && <span style={{ marginInlineStart: 2 }}>{lang === 'ar' ? 'تصدير' : 'Export'}</span>}
              </button>
            )}

            {/* My Orders — OTP-gated for data security */}
            {retailerProfile && _routeToken && (
              <button onClick={() => {
                if (otpVerified || impersonationVerified) { setView('myorders'); }
                else { setOtpModal(true); setOtpSent(false); setOtpCode(''); setOtpError(''); }
              }}
                style={{
                  background: view === 'myorders' ? 'var(--sedra-olive)' : 'rgba(45,58,40,0.08)',
                  border: view === 'myorders' ? 'none' : '1.5px solid rgba(45,58,40,0.15)',
                  color: view === 'myorders' ? '#fff' : 'var(--sedra-forest)',
                  padding: isMobile ? '8px 12px' : '9px 18px',
                  borderRadius: 100, cursor: 'pointer',
                  fontSize: isMobile ? 12 : 13, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                <Icon name="Receipt" size={16} />{!isMobile && <span style={{ marginInlineStart: 2 }}>{t.myOrders}</span>}
                {myOrders.length > 0 && (
                  <span style={{ background: 'var(--sedra-sage-wash)', color: 'var(--sedra-forest)', borderRadius: 12, padding: '1px 7px', fontSize: 11, fontWeight: 900 }}>
                    {myOrders.length}
                  </span>
                )}
              </button>
            )}
            <button onClick={() => setView('cart')}
              style={{
                background: cartCount > 0 ? 'var(--sedra-olive)' : 'rgba(45,58,40,0.08)',
                border: cartCount > 0 ? 'none' : '1.5px solid rgba(45,58,40,0.15)',
                color: cartCount > 0 ? '#fff' : 'var(--sedra-forest)',
                padding: isMobile ? '8px 14px' : '9px 20px',
                borderRadius: 100, cursor: 'pointer',
                fontSize: isMobile ? 13 : 14, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 6,
                transition: 'all 0.2s',
                animation: flashId ? 'sdra-cartBounce 0.45s ease' : 'none',
              }}>
              <span style={{ display: 'flex' }}><ShoppingCart size={isMobile ? 16 : 18} aria-hidden="true" /></span>
              {!isMobile && <span>{t.cart}</span>}
              {cartCount > 0 && (
                <span style={{ background: 'var(--sedra-sage-wash)', color: 'var(--sedra-forest)', borderRadius: 12, padding: '1px 8px', fontSize: 12, fontWeight: 900, minWidth: 22, textAlign: 'center' }}>
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── Phase 7: Welcome Banner (identified retailer tokens only) ── */}
      {retailerProfile && (
        <div style={{
          background: 'linear-gradient(90deg, var(--sedra-forest) 0%, var(--sedra-forest-soft) 100%)',
          color: '#fff',
          padding: isMobile ? '10px 18px' : '11px 28px',
          marginTop: isMobile ? 60 : 72,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          fontSize: isMobile ? 13 : 14,
        }}>
          <div style={{ maxWidth: 1140, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', direction: dir }}>
            <span style={{ fontWeight: 700, color: 'var(--sedra-sage-wash)' }}>{retailerProfile.company_name}</span>
            <span style={{ color: '#8fad78', fontSize: 12 }}>
              {lang === 'ar' ? `• الطلب باسم: ${retailerProfile.contact_name}` : `• Ordering as: ${retailerProfile.contact_name}`}
            </span>
          </div>
        </div>
      )}

      {/* ── HERO BANNER — Ken Burns Carousel ── */}
      {view === 'catalogue' && (() => {
        // No hero image configured. Deliberately src-less: these used to point at
        // three PNGs (6.6 MB + 4.9 MB + 1.8 MB) in the abandoned radcfbsezwxujmngxlpp
        // project, which is what exhausted the egress quota on 22 Aug 2026. A single
        // src-less slide renders HeroBanner's branded panel with no network request
        // and no pointless carousel. Upload real banners via Admin -> Banners.
        const FALLBACK_SLIDES = [
          { src: null, titleEn: '', titleAr: '',
            subEn: type === 'vat' ? 'Prices include 14% VAT' : type === 'noprice' ? 'Browse Products — No Prices' : 'Official Wholesale Prices',
            subAr: type === 'vat' ? 'الأسعار شاملة ضريبة القيمة المضافة ١٤٪' : type === 'noprice' ? 'تصفح المنتجات — بدون أسعار' : 'أسعار الجملة الرسمية' },
        ];
        const slides = (banners && banners.length > 0) ? banners.map(b => ({ src: b.image_url, titleEn: 'Sedra Home', titleAr: 'سدرة هوم', subEn: '', subAr: '' })) : FALLBACK_SLIDES;
        return (
          <ErrorBoundary name="hero" lang={lang} fallback={
            <div style={{ background: 'var(--sedra-forest)', minHeight: 120 }} />
          }>
            <HeroBanner
              slides={slides}
              lang={lang}
              isMobile={isMobile}
              type={type}
              onApply={type === 'noprice' ? () => { setShowApplyModal(true); setApplySuccess(false); setApplyError(''); } : null}
            />
          </ErrorBoundary>
        );
      })()}

      {view === 'confirm' && (
        <div style={{ maxWidth: 500, margin: '80px auto', background: '#fff', borderRadius: 16, padding: 40, textAlign: 'center', boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'var(--sedra-forest)', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'sedra-circleIn 0.45s cubic-bezier(0.22,1,0.36,1) both' }}>
            <svg width="38" height="28" viewBox="0 0 38 28" fill="none">
              <path d="M3 14 L14 25 L35 3" stroke="var(--sedra-sage-wash)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="48" strokeDashoffset="48" style={{ animation: 'sedra-checkDraw 0.4s 0.35s ease forwards' }}/>
            </svg>
          </div>
          <h2 style={{ color: 'var(--sedra-forest)', marginBottom: 8, fontWeight: 800 }}>{t.orderSent}</h2>
          <p style={{ color: '#666', marginBottom: 16 }}>{t.orderThankYou}</p>
          <p style={{ color: '#888', fontSize: 14 }}>{t.orderRef}: <strong>{orderId}</strong></p>
          <button onClick={() => setView('catalogue')} style={{ marginTop: 24, padding: '12px 32px', borderRadius: 100, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700, boxShadow: '0 3px 12px rgba(45,58,40,0.22)' }}>
            {t.continueShopping}
          </button>
        </div>
      )}

      {view === 'myorders' && (
        <div style={{ maxWidth: 700, margin: isMobile ? '16px auto' : '32px auto', padding: isMobile ? '0 10px' : '0 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <button onClick={() => setView('catalogue')}
              style={{ background: 'var(--sedra-cream)', border: '1.5px solid var(--sedra-line)', color: 'var(--sedra-forest-soft)', cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '9px 18px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name={dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'} size={16} /> {t.continueShopping}
            </button>
            <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 22, color: 'var(--sedra-forest)', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}><Icon name="Receipt" size={20} /> {t.myOrders}</h2>
          </div>
          {myOrdersLoading ? (
            <div style={{ color: '#aaa', textAlign: 'center', padding: 40 }}>{t.loading}</div>
          ) : myOrders.length === 0 ? (
            <div style={{ color: '#bbb', textAlign: 'center', padding: 60, fontSize: 15 }}>{t.noOrders}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {myOrders.map(ord => {
                const sc = ord.status === 'new' ? { bg: '#dbeafe', color: '#1d4ed8' }
                  : ord.status === 'confirmed' ? { bg: 'var(--status-success-bg)', color: 'var(--status-success)' }
                  : ord.status === 'cancelled' ? { bg: 'var(--status-danger-bg)', color: 'var(--status-danger)' }
                  : { bg: 'var(--sedra-sage-wash)', color: 'var(--sedra-sage-ink)' };
                const isExpanded = expandedOrder === ord.id;
                const detail     = orderDetails[ord.id];
                return (
                  <div key={ord.id} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
                    {/* ── Summary row ── */}
                    <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, cursor: 'pointer' }}
                      onClick={() => {
                        const next = isExpanded ? null : ord.id;
                        setExpandedOrder(next);
                        if (next) fetchDetail(ord.id);
                      }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sedra-forest)', fontFamily: 'monospace' }}>{ord.id}</div>
                        <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                          {new Date(ord.created_at).toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' })}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{ord.item_count} {lang === 'ar' ? 'صنف' : 'items'}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {showPrice && ord.total != null && (
                          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--sedra-forest-soft)' }}>{fmt(ord.total)}</div>
                        )}
                        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 700, background: sc.bg, color: sc.color }}>{ord.status}</span>
                        <span style={{ fontSize: 13, color: '#aaa', userSelect: 'none' }}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* ── Expanded detail ── */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid #f0ebe3', padding: '14px 18px', background: '#fafaf8' }}>

                        {/* ── Order Status Timeline ── */}
                        {ord.status === 'cancelled' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '9px 14px', background: 'var(--status-danger-bg)', borderRadius: 8 }}>
                            <Icon name="Ban" size={16} color="var(--status-danger)" />
                            <span style={{ fontWeight: 700, color: 'var(--status-danger)', fontSize: 13 }}>{lang === 'ar' ? 'تم إلغاء الطلب' : 'Order Cancelled'}</span>
                          </div>
                        ) : (() => {
                          const STAGES = [
                            { key: 'new',       en: 'Received',   ar: 'تم الاستلام',   icon: 'Download' },
                            { key: 'confirmed', en: 'Confirmed',  ar: 'مؤكد',           icon: 'CheckCircle2' },
                            { key: 'processing',en: 'Processing', ar: 'قيد التجهيز',    icon: 'Settings' },
                            { key: 'shipped',   en: 'Shipped',    ar: 'في الطريق',      icon: 'Truck' },
                            { key: 'delivered', en: 'Delivered',  ar: 'تم التسليم',     icon: 'Package' },
                          ];
                          const effStatus = ord.status === 'seen' ? 'new' : ord.status;
                          const curIdx    = STAGES.findIndex(s => s.key === effStatus);
                          return (
                            <div style={{ marginBottom: 16, overflowX: 'auto' }}>
                              <div style={{ display: 'flex', alignItems: 'center', minWidth: isMobile ? 340 : 'auto' }}>
                                {STAGES.map((s, i) => {
                                  const isDone    = i < curIdx;
                                  const isCurrent = i === curIdx;
                                  const isFuture  = i > curIdx;
                                  return (
                                    <React.Fragment key={s.key}>
                                      {/* Step node */}
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                                        <div style={{
                                          width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14,
                                          background: isDone ? 'var(--sedra-forest-soft)' : isCurrent ? 'var(--sedra-forest)' : '#e8e4de',
                                          border: isCurrent ? '3px solid var(--sedra-sage-ink)' : 'none',
                                          boxShadow: isCurrent ? '0 0 0 3px rgba(74,82,64,0.18)' : 'none',
                                          transition: 'all 0.3s',
                                        }}>
                                          {isDone
                                            ? <Icon name="Check" size={16} color="var(--sedra-sage-wash)" strokeWidth={2.5} />
                                            : <span style={{ filter: isFuture ? 'grayscale(1) opacity(0.4)' : 'none', display: 'flex' }}><Icon name={s.icon} size={16} color={isCurrent ? 'var(--sedra-cream)' : 'var(--sedra-ink-muted)'} /></span>}
                                        </div>
                                        <div style={{ fontSize: isMobile ? 9 : 10, marginTop: 5, fontWeight: isCurrent ? 800 : 500, color: isDone || isCurrent ? 'var(--sedra-forest)' : '#bbb', textAlign: 'center', maxWidth: 56, lineHeight: 1.2 }}>
                                          {lang === 'ar' ? s.ar : s.en}
                                        </div>
                                      </div>
                                      {/* Connector line */}
                                      {i < STAGES.length - 1 && (
                                        <div style={{ flex: 1, height: 3, background: isDone ? 'var(--sedra-forest-soft)' : '#e8e4de', margin: '0 4px', marginBottom: 20, borderRadius: 2, transition: 'background 0.3s' }} />
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {detail?.loading ? (
                          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '12px 0' }}>…</div>
                        ) : detail?.items?.length ? (
                          <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                              <thead><tr style={{ background: 'var(--sedra-sage-wash)' }}>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: '#666', fontWeight: 700 }}>SKU</th>
                                <th style={{ padding: '6px 10px', textAlign: 'left', color: '#666', fontWeight: 700 }}>{lang === 'ar' ? 'المنتج' : 'Product'}</th>
                                <th style={{ padding: '6px 10px', textAlign: 'center', color: '#666', fontWeight: 700 }}>{lang === 'ar' ? 'الكمية' : 'Qty'}</th>
                                {showPrice && <th style={{ padding: '6px 10px', textAlign: 'right', color: '#666', fontWeight: 700 }}>{lang === 'ar' ? 'الإجمالي' : 'Total'}</th>}
                              </tr></thead>
                              <tbody>
                                {detail.items.map((it, i) => (
                                  <tr key={i} style={{ borderTop: '1px solid #f0ebe3' }}>
                                    <td style={{ padding: '6px 10px', color: '#888', fontFamily: 'monospace', fontSize: 11 }}>{it.sku}</td>
                                    <td style={{ padding: '6px 10px', color: 'var(--sedra-forest)', fontWeight: 600 }}>{lang === 'ar' && it.name_ar ? it.name_ar : it.name_en}{it.variant_label && <span style={{ marginInlineStart: 5, fontSize: 10, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-sage-ink)', borderRadius: 4, padding: '1px 5px' }}>{it.variant_label}</span>}</td>
                                    <td style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 700 }}>{it.qty}</td>
                                    {showPrice && <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--sedra-forest-soft)', fontWeight: 700 }}>{it.price != null ? fmt(it.price * it.qty) : '—'}</td>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {/* Export this order as a printable sheet WITH product
                                pictures — same layout as the price quote. */}
                            <button
                              onClick={() => exportOrderSheet(ord)}
                              disabled={quoteGenerating}
                              style={{ marginTop: 12, marginInlineEnd: 8, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-forest)', border: '1.5px solid var(--sedra-line)', borderRadius: 100, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: quoteGenerating ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Icon name="Download" size={15} />
                              {quoteGenerating
                                ? (lang === 'ar' ? 'جارٍ التجهيز…' : 'Preparing…')
                                : (lang === 'ar' ? 'تصدير بالصور' : 'Export with pictures')}
                            </button>
                            {/* Same order as a spreadsheet, for re-ordering and
                                importing into the buyer's own system. */}
                            <button
                              onClick={() => exportOrderExcel(ord)}
                              disabled={excelGenerating}
                              style={{ marginTop: 12, marginInlineEnd: 8, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-forest)', border: '1.5px solid var(--sedra-line)', borderRadius: 100, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: excelGenerating ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Icon name="FileSpreadsheet" size={15} />
                              {excelGenerating
                                ? (lang === 'ar' ? 'جارٍ التجهيز…' : 'Preparing…')
                                : (lang === 'ar' ? 'تصدير Excel' : 'Export Excel')}
                            </button>
                            {/* Reorder button */}
                            {ord.status !== 'cancelled' && (
                              <button
                                onClick={() => {
                                  let added = 0, skipped = 0;
                                  detail.items.forEach(it => {
                                    const prod = products.find(p => p.sku === it.sku && p.is_active !== false);
                                    if (prod) {
                                      addToCart(prod, it.qty);
                                      added++;
                                    } else {
                                      skipped++;
                                    }
                                  });
                                  const msg = lang === 'ar'
                                    ? `تمت إضافة ${added} صنف إلى السلة${skipped ? ` (${skipped} غير متاح)` : ''}`
                                    : `${added} items added to cart${skipped ? ` (${skipped} unavailable)` : ''}`;
                                  setToast(msg); setTimeout(() => setToast(''), 3500);
                                  setView('cart');
                                }}
                                style={{ marginTop: 12, padding: '10px 22px', borderRadius: 100, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Icon name="RefreshCw" size={14} /> {lang === 'ar' ? 'إعادة الطلب' : 'Reorder'}
                              </button>
                            )}
                          </>
                        ) : (
                          <div style={{ color: '#bbb', fontSize: 13 }}>{lang === 'ar' ? 'لا تفاصيل متاحة' : 'No details available'}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === 'cart' && (
        <div style={{ maxWidth: 700, margin: isMobile ? '16px auto' : '32px auto', padding: isMobile ? '0 10px' : '0 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <button onClick={() => setView('catalogue')}
              style={{ background: 'var(--sedra-cream)', border: '1.5px solid var(--sedra-line)', color: 'var(--sedra-forest-soft)', cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: '9px 18px', borderRadius: 100, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name={dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'} size={16} /> {t.continueShopping}
            </button>
            {/* Sync indicator — only for token holders */}
            {_routeToken && (
              <span style={{ fontSize: 12, color: isSynced === true ? 'var(--status-success)' : '#aaa', fontWeight: 600, transition: 'color 0.3s', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {isSynced === false && <><Icon name="Loader2" size={12} /> {t.syncSaving}</>}
                {isSynced === true && <><Icon name="Check" size={12} /> {t.syncSaved}</>}
              </span>
            )}
          </div>
          <h2 style={{ marginTop: 0, fontSize: isMobile ? 18 : 24 }}>{t.cart}</h2>
          {cart.length === 0 ? <p style={{ color: '#aaa' }}>{t.emptyCart}</p> : (
            <>
              {cart.map(item => (
                <div key={item.id} style={{ background: '#fff', borderRadius: 10, padding: '16px', marginBottom: 10, display: 'flex', gap: 16, alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
                  {(item.image_url || item.image) && <img src={item.image_url || item.image} alt="" onError={e => { e.target.onerror = null; e.target.src = IMG_PH; }} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{lang === 'ar' && item.name_ar ? item.name_ar : item.name_en}</div>
                    <div style={{ color: '#888', fontSize: 13 }}>{item.sku}{item.variant_label ? <span style={{ marginInlineStart: 6, background: 'var(--sedra-sage-wash)', color: 'var(--sedra-forest-soft)', borderRadius: 6, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>{item.variant_label}</span> : null}</div>
                    {showPrice && <div style={{ color: 'var(--sedra-sage-ink)', fontWeight: 700 }}>{fmt(cartGetPrice(item))} × {item.qty}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button onClick={() => updateQty(item._key || item.id, item.qty - 1)} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--sedra-line)', background: '#fff', cursor: 'pointer', fontSize: 16 }}>−</button>
                    <input type="number" className="sdra-qtyInput" value={item.qty} min={item.min_qty||1} step={item.min_qty||1} onChange={e=>updateQty(item._key || item.id,Math.max(item.min_qty||1,parseInt(e.target.value)||(item.min_qty||1)))} style={{width:52,textAlign:'center',fontWeight:700,border:'1px solid var(--sedra-line)',borderRadius:6,padding:'3px 4px',fontSize:14,background:'#fff'}} />
                    <button onClick={() => updateQty(item._key || item.id, item.qty + 1)} style={{ width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--sedra-line)', background: '#fff', cursor: 'pointer', fontSize: 16 }}>+</button>
                  </div>
                </div>
              ))}
              {showPrice && <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 18, margin: '16px 0' }}>Total: {fmt(cartTotal)}</div>}
              {showVat && <div style={{ textAlign: 'right', fontSize: 13, color: '#888', marginBottom: 16 }}>Includes 14% VAT</div>}
              {/* Share order with a manager + Download Quote PDF */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button onClick={shareOrder} disabled={cart.length === 0}
                  title={lang === 'ar' ? 'أرسل رابط الطلب (بالصور) إلى مديرك' : 'Send an order link (with images) to your manager'}
                  style={{ padding:'9px 20px', borderRadius:8, background: cart.length === 0 ? '#ccc' : 'var(--sedra-pistachio-deep)', color:'var(--sedra-cream)', border:'none', cursor: cart.length === 0 ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13, display:'inline-flex', alignItems:'center', gap:7 }}>
                  <Icon name="Share2" size={16} /> {lang === 'ar' ? 'مشاركة مع المدير' : 'Share with manager'}
                </button>
                <button onClick={generateQuote} disabled={quoteGenerating || cart.length === 0}
                  style={{ padding:'9px 20px', borderRadius:8, background: quoteGenerating || cart.length === 0 ? '#ccc' : 'var(--sedra-olive)', color:'#fff', border:'none', cursor: quoteGenerating || cart.length === 0 ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13, display:'inline-flex', alignItems:'center', gap:7 }}>
                  {quoteGenerating
                    ? <Icon name="Loader2" size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                    : <Icon name="FileText" size={16} />}
                  {quoteGenerating ? '…' : (lang === 'ar' ? 'تحميل عرض السعر (PDF)' : 'Download Quote PDF')}
                </button>
                {/* Excel alongside the PDF — buyers re-quantify lines and import
                    them into their own systems, which a PDF makes them retype. */}
                <button onClick={() => downloadExcel()} disabled={excelGenerating || cart.length === 0}
                  title={lang === 'ar' ? 'تحميل الطلب كملف Excel قابل للتعديل' : 'Download the order as an editable Excel file'}
                  style={{ padding:'9px 20px', borderRadius:8, background: excelGenerating || cart.length === 0 ? '#ccc' : 'var(--sedra-forest)', color:'#fff', border:'none', cursor: excelGenerating || cart.length === 0 ? 'not-allowed' : 'pointer', fontWeight:700, fontSize:13, display:'inline-flex', alignItems:'center', gap:7 }}>
                  {excelGenerating
                    ? <Icon name="Loader2" size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                    : <Icon name="FileSpreadsheet" size={16} />}
                  {excelGenerating ? '…' : (lang === 'ar' ? 'تحميل Excel' : 'Download Excel')}
                </button>
              </div>

              <div style={{ background: '#fff', borderRadius: 10, padding: isMobile ? 14 : 24, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: isMobile ? 16 : 18 }}>{t.checkout}</h3>
                {/* Phase 7: Profile-locked fields for identified retailers */}
                {retailerProfile ? (
                  <div style={{ background: '#f7f9f5', borderRadius: 9, padding: '12px 16px', marginBottom: 12, border: '1.5px solid #c8dab0' }}>
                    <div style={{ fontSize: 11, color: 'var(--sedra-sage-ink)', fontWeight: 700, textTransform: lang === 'ar' ? 'none' : 'uppercase', letterSpacing: lang === 'ar' ? 'normal' : 0.8, marginBottom: 8 }}>
                      <Icon name="Lock" size={14} /> {lang === 'ar' ? 'معلومات الحساب (مؤمّنة)' : 'Account Details (Locked)'}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                      {[
                        [lang === 'ar' ? 'اسم الشركة' : 'Company', retailerProfile.company_name],
                        [lang === 'ar' ? 'المسؤول' : 'Contact', retailerProfile.contact_name],
                        [lang === 'ar' ? 'الهاتف' : 'Phone', retailerProfile.phone],
                        [lang === 'ar' ? 'البريد' : 'Email', retailerProfile.email],
                      ].map(([lbl, val]) => (
                        <div key={lbl}>
                          <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{lbl}</div>
                          <div style={{ fontWeight: 600, color: 'var(--sedra-forest)' }}>{val || '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                  {(retailerProfile
                    // Identified retailers: only need whatsapp, address, notes
                    ? [['WhatsApp', 'whatsapp'], [t.address, 'address'], [t.notes, 'notes']]
                    // Guest retailers: full form
                    : [[t.businessName, 'business_name'], [t.contactName, 'contact_name'], [t.phone, 'phone'], ['WhatsApp', 'whatsapp'], [t.email, 'email'], [t.address, 'address'], [t.notes, 'notes']]
                  ).map(([label, key]) => (
                    <div key={key} style={{ gridColumn: key === 'address' || key === 'notes' ? '1/-1' : undefined }}>
                      <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>{label}{(!retailerProfile && (key === 'contact_name' || key === 'phone')) ? ' *' : ''}</label>
                      {key === 'notes' ? (
                        <textarea style={{ ...inputStyle, minHeight: 60 }} value={orderForm[key]} onChange={e => setOrderForm(f => ({ ...f, [key]: e.target.value }))} />
                      ) : (
                        <input style={inputStyle} value={orderForm[key]} onChange={e => setOrderForm(f => ({ ...f, [key]: e.target.value }))} />
                      )}
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, padding: '14px', background: '#f9f7f4', borderRadius: 10 }}>
                  <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 10, fontWeight: 600 }}>
                    {lang === 'ar' ? 'طريقة الدفع' : 'Payment Method'} *
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[
                      { val: 'instapay', icon: 'CreditCard', en: 'Instapay',       ar: 'إنستاباي' },
                      { val: 'bank',     icon: 'Landmark',   en: 'Bank Transfer',  ar: 'تحويل بنكي' },
                      { val: 'cheque',   icon: 'FileText',   en: 'Cheque',         ar: 'شيك' },
                      { val: 'cash',     icon: 'Banknote',   en: 'Cash',           ar: 'نقداً' },
                    ].map(({ val, icon, en: enL, ar: arL }) => (
                      <button key={val} type="button"
                        onClick={() => setOrderForm(f => ({ ...f, payment_method: val }))}
                        style={{
                          padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                          border: '2px solid', fontSize: 13, fontWeight: 600,
                          borderColor: orderForm.payment_method === val ? 'var(--sedra-sage-ink)' : 'var(--sedra-line)',
                          background: orderForm.payment_method === val ? 'var(--sedra-sage-wash)' : '#fff',
                          color: orderForm.payment_method === val ? 'var(--sedra-forest-soft)' : '#888',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}>
                        <Icon name={icon} size={16} /> {lang === 'ar' ? arL : enL}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={placeOrder} disabled={submitting || (!retailerProfile && (!orderForm.contact_name || !orderForm.phone))}
                  style={{ marginTop: 16, width: '100%', padding: '14px', borderRadius: 100, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 700, opacity: (!retailerProfile && (!orderForm.contact_name || !orderForm.phone)) ? 0.5 : 1, boxShadow: '0 3px 12px rgba(45,58,40,0.22)' }}>
                  {submitting ? '...' : t.placeOrder}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {view === 'catalogue' && (
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: isMobile ? '16px 12px 96px' : '28px 20px 40px' }}>

          {/* Search bar + Sort */}
          <div style={{ marginBottom: search ? 6 : 14, display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <span style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [dir === 'rtl' ? 'right' : 'left']: 14, color: 'var(--sedra-ink-muted)', display: 'flex', pointerEvents: 'none' }}><Search size={16} aria-hidden="true" /></span>
              <input placeholder={t.searchProducts} value={search} onChange={e => setSearch(e.target.value)}
                style={{ ...inputStyle, background: '#fff', paddingLeft: dir === 'rtl' ? (search ? 36 : 14) : 42, paddingRight: dir === 'rtl' ? 42 : (search ? 36 : 14), boxShadow: '0 1px 6px rgba(0,0,0,0.07)', borderColor: search ? 'var(--sedra-forest-soft)' : 'var(--sedra-line)', fontSize: 14, height: 44, borderRadius: 12, transition: 'border-color 0.2s' }} />
              {search && (
                <button onClick={() => setSearch('')}
                  style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', [dir === 'rtl' ? 'left' : 'right']: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '2px 4px', display: 'flex' }}
                  title={lang === 'ar' ? 'مسح البحث' : 'Clear search'}><Icon name="X" size={16} /></button>
              )}
            </div>
            <select value={sortOrder} onChange={e => { setSortOrder(e.target.value); setVisibleCount(BATCH_SIZE); window.scrollTo({top:0, behavior:'smooth'}); }}
              style={{ height: 44, padding: '0 12px', borderRadius: 12, border: '1.5px solid var(--sedra-line)', background: '#fff', color: 'var(--sedra-forest-soft)', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', flexShrink: 0 }}>
              <option value="newest">{lang === 'ar' ? 'الأحدث' : 'Newest'}</option>
              <option value="oldest">{lang === 'ar' ? 'الأقدم' : 'Oldest'}</option>
            </select>
          </div>
          {/* Result count — only shown when search is active */}
          {search && (
            <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--sedra-sage-ink)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{filtered.length > 0
                ? (lang === 'ar' ? `${filtered.length} نتيجة لـ "${search}"` : `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${search}"`)
                : (lang === 'ar' ? `لا نتائج لـ "${search}"` : `No results for "${search}"`)}</span>
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 11, textDecoration: 'underline', padding: 0 }}>
                {lang === 'ar' ? 'مسح' : 'Clear'}
              </button>
            </div>
          )}

          {/* Category chips — Apple segmented control */}
          <div ref={categoryTabsRef} className="sedra-cat-strip" style={{ marginBottom: 18, display: 'flex', gap: 8 }}>
            {categories.map(c => (
              <div key={c} data-active={category === c ? 'true' : 'false'}>
                <CategoryPill
                  active={category === c}
                  onClick={() => setCategory(c)}
                  label={c === 'all' ? t.allCategories : c === 'new' ? t.newArrivals : (lang === 'ar' ? (catArMap[c] || catLabel(c, 'ar')) : catLabel(c, 'en'))}
                  iconElement={c === 'all' ? null : c === 'new' ? <Sparkles size={16} aria-hidden="true" /> : <CategoryIcon category={c} size={16} />}
                  style={{ minHeight: 36, padding: '7px 13px', fontSize: 13 }}
                />
              </div>
            ))}
          </div>

          {/* ── PRODUCT GRID ── */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(230px, 1fr))', gap: isMobile ? 12 : 22 }}>
            {displayed.map(p => {
              const added = flashId === p.id;
              return (
                <div key={p.id} className="sdra-card sedra-card-new"
                  onClick={() => openProduct(p)}
                  style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>

                  {/* Image area */}
                  <div style={{ height: isMobile ? 140 : 200, background: 'var(--sedra-image-well)', overflow: 'hidden', position: 'relative' }}>
                    {(p.image_url || p.image)
                      ? <img src={thumbUrl(p.image_url || p.image)} alt={p.name_en} loading="lazy" className="sdra-img"
                          // Two-stage fallback: a product added after the thumbnail
                          // run has no thumbs/ copy, so drop to the full image before
                          // giving up on the placeholder.
                          onError={e => {
                            const full = p.image_url || p.image;
                            if (full && e.target.src !== full) { e.target.src = full; return; }
                            e.target.onerror = null; e.target.src = IMG_PH;
                          }}
                          style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}><HomeIcon size={isMobile ? 32 : 44} aria-hidden="true" /></div>}

                    {/* "New" badge — shown for products added within NEW_DAYS */}
                    {p.created_at && new Date(p.created_at) >= NEW_CUTOFF && (
                      <div style={{ position: 'absolute', top: 7, [dir === 'rtl' ? 'left' : 'right']: 7 }}>
                        <Badge tone="tan" style={{ boxShadow: '0 2px 8px rgba(0,42,23,0.25)', letterSpacing: lang === 'ar' ? 0 : 0.5, textTransform: lang === 'ar' ? 'none' : 'uppercase' }}>
                          {lang === 'ar' ? 'جديد' : 'New'}
                        </Badge>
                      </div>
                    )}
                    {/* Photos badge — only visible after detail is lazy-loaded */}
                    {p._fullLoaded && Array.isArray(p.images) && p.images.length > 1 && (
                      <div style={{ position: 'absolute', bottom: 7, right: dir === 'rtl' ? 'auto' : 7, left: dir === 'rtl' ? 7 : 'auto', background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(4px)', color: '#fff', borderRadius: 20, padding: '3px 9px', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Camera size={12} aria-hidden="true" /> {p.images.length}
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: isMobile ? '10px 11px 12px' : '13px 15px 15px' }}>
                    {p.category && (
                      <div style={{ fontSize: 9, color: 'var(--sedra-sage-ink)', fontWeight: 700, textTransform: lang === 'ar' ? 'none' : 'uppercase', letterSpacing: lang === 'ar' ? 'normal' : 1, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}><CategoryIcon category={p.category} size={11} /> {lang === 'ar' ? (p.category_ar || catLabel(p.category, 'ar')) : catLabel(p.category, 'en')}</div>
                    )}
                    <div style={{ fontWeight: lang === 'ar' ? 700 : 600, fontSize: isMobile ? 12.5 : 14, color: 'var(--sedra-forest)', marginBottom: 2, lineHeight: 1.4, minHeight: isMobile ? 34 : 40, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {lang === 'ar' && p.name_ar ? p.name_ar : p.name_en}
                    </div>
                    {/* Marketplace: vendor branding — "Sold by X via Sedra Home" */}
                    {(p.vendor_name_en || p.vendor_name_ar) && (
                      <div style={{ fontSize: 10, color: 'var(--sedra-sage-ink)', fontWeight: 700, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Store size={11} aria-hidden="true" /> {lang === 'ar'
                          ? `يُباع بواسطة ${p.vendor_name_ar || p.vendor_name_en} عبر سدرة هوم`
                          : `Sold by ${p.vendor_name_en} via Sedra Home`}
                      </div>
                    )}
                    {/* B2B info row — SKU · carton · min qty (BRAND.md §8.2) */}
                    {(p.sku || p.carton_qty > 1 || p.min_qty > 1) && (
                      <div style={{ fontSize: 10, color: 'var(--sedra-ink-muted)', fontWeight: 500, marginBottom: 2, display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
                        {p.sku && <span dir="ltr">SKU {p.sku}</span>}
                        {p.carton_qty > 1 && <span>{lang === 'ar' ? `الكرتونة ${p.carton_qty}` : `Carton ${p.carton_qty}`}</span>}
                        {p.min_qty > 1 && <span>{lang === 'ar' ? `أقل كمية ${p.min_qty}` : `Min ${p.min_qty}`}</span>}
                      </div>
                    )}
                    {/* Stock chip */}
                    {stockTracking && p.stock !== undefined && p.stock !== null && (
                      Number(p.stock) <= 0 ? (
                        <StatusChip state="out" lang={lang} label={lang === 'ar' ? 'غير متوفر حالياً' : 'Out of stock'} style={{ fontSize: 9.5, padding: '2px 8px', marginBottom: 2 }} />
                      ) : Number(p.stock) <= (Number(p.min_qty) || 1) * 2 ? (
                        <StatusChip state="low" lang={lang} style={{ fontSize: 9.5, padding: '2px 8px', marginBottom: 2 }} />
                      ) : null
                    )}
                    {showPrice && (
                      <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: 'var(--sedra-forest)', margin: '6px 0 10px', letterSpacing: -0.3 }}>
                        {showVat ? fmtVat(p.price) : fmt(p.price)}
                      </div>
                    )}
                    <button className="sedra-pill-add"
                      onClick={e => { e.stopPropagation(); if (p.has_variants) { openProduct(p); } else { addToCart(p); } }}
                      style={{
                        width: '100%',
                        padding: isMobile ? '8px 0' : '10px 0',
                        borderRadius: 100,
                        background: added ? 'var(--sedra-olive-deep)' : 'var(--sedra-olive)',
                        color: '#fff',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: isMobile ? 11 : 13,
                        fontWeight: 700,
                        letterSpacing: 0.2,
                        height: 38,
                        boxShadow: '0 3px 12px rgba(45,58,40,0.22)',
                        animation: added ? 'sdra-flashRing 0.7s ease' : 'none',
                      }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {added
                          ? (<><Icon name="Check" size={14} /> {lang === 'ar' ? 'تمت الإضافة' : 'Added!'}</>)
                          : p.has_variants
                            ? (lang === 'ar' ? 'اختر الاختيار' : 'Select Variant')
                            : (<><ShoppingCart size={14} aria-hidden="true" /> {t.addToCart}</>)}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          {filtered.length === 0 && <div style={{ textAlign: 'center', color: '#aaa', padding: 60, fontSize: 15 }}>{t.noProducts}</div>}

          {/* ── Infinite scroll: spinner IS the sentinel — observer attaches when visible, detaches on unmount ── */}
          {visibleCount < filtered.length && (
            <div ref={sentinelCallback} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '28px 0 16px' }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2.5px solid var(--sedra-line)', borderTopColor: 'var(--sedra-forest-soft)', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, color: '#aaa' }}>{lang === 'ar' ? 'تحميل المزيد...' : 'Loading more...'}</span>
            </div>
          )}
          {visibleCount >= filtered.length && filtered.length > 24 && (
            <div style={{ textAlign: 'center', padding: '20px 0 8px', fontSize: 12, color: '#ccc' }}>
              {lang === 'ar' ? `تم عرض جميع المنتجات (${filtered.length})` : `All ${filtered.length} products shown`}
            </div>
          )}
        </div>
      )}

      {/* ── FOOTER ── */}
      {view === 'catalogue' && (
        <footer style={{
          background: '#52623A',
          color: '#fff',
          padding: isMobile ? '52px 20px 36px' : '72px 28px 48px',
          fontFamily: lang === 'ar' ? "'Almarai','Tajawal',sans-serif" : "'Inter','Cairo',sans-serif",
          direction: lang === 'ar' ? 'rtl' : 'ltr',
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1.3fr 0.9fr 0.9fr 0.9fr',
              gap: isMobile ? 40 : 40,
              marginBottom: 52,
            }}>

              {/* Brand column */}
              <div>
                <img
                  src={LOGO_URL}
                  alt="Sedra Home"
                  style={{ height: 60, width: 'auto', marginBottom: 20, display: 'block' }}
                />
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.60)', lineHeight: 1.9, margin: '0 0 20px', maxWidth: 280 }}>
                  {lang === 'ar'
                    ? 'مستلزمات المنزل الراقية — كتالوج الجملة الرسمي للتجار المعتمدين'
                    : 'Premium home essentials — official wholesale catalogue for approved retailers.'}
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  <a href="https://sedrahome.com" target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.10)', color: '#fff', textDecoration: 'none', fontSize: 17, transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(201,168,122,0.30)'}
                    onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.10)'}><Icon name="Globe" size={17} /></a>
                  <a href={footerInfo.instagram || 'https://www.instagram.com/sedrahome.eg'} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.10)', color: '#fff', textDecoration: 'none', fontSize: 17, transition: 'background 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(201,168,122,0.30)'}
                    onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.10)'}><Icon name="Camera" size={17} /></a>
                  {footerInfo.whatsapp && (
                    <a href={`https://wa.me/${footerInfo.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: '50%', background: 'rgba(255,255,255,0.10)', color: '#fff', textDecoration: 'none', fontSize: 17, transition: 'background 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(201,168,122,0.30)'}
                      onMouseLeave={e => e.currentTarget.style.background='rgba(255,255,255,0.10)'}><Icon name="MessageCircle" size={17} /></a>
                  )}
                </div>
              </div>

              {/* Contact column */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: lang === 'ar' ? 'normal' : '0.14em', textTransform: lang === 'ar' ? 'none' : 'uppercase', color: 'var(--sedra-tan)', marginBottom: 22 }}>
                  {lang === 'ar' ? 'تواصل معنا' : 'Contact Us'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {footerInfo.phone && (
                    <a href={`tel:${footerInfo.phone}`} style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}><Icon name="Phone" size={15} /></span>
                      <span>{footerInfo.phone}</span>
                    </a>
                  )}
                  <a href={`mailto:${footerInfo.email}`} style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}><Icon name="Mail" size={15} /></span>
                    <span>{footerInfo.email}</span>
                  </a>
                  {footerInfo.address && (
                    <div style={{ color: 'rgba(255,255,255,0.60)', fontSize: 14, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}><Icon name="MapPin" size={15} /></span>
                      <span style={{ lineHeight: 1.7 }}>{footerInfo.address}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Store column */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: lang === 'ar' ? 'normal' : '0.14em', textTransform: lang === 'ar' ? 'none' : 'uppercase', color: 'var(--sedra-tan)', marginBottom: 22 }}>
                  {lang === 'ar' ? 'روابط سريعة' : 'Quick Links'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <a href="https://sedrahome.com" target="_blank" rel="noopener noreferrer"
                    style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    {lang === 'ar' ? 'المتجر الإلكتروني' : 'Online Store'} <Icon name="ExternalLink" size={13} />
                  </a>
                  <a href={footerInfo.instagram || 'https://www.instagram.com/sedrahome'} target="_blank" rel="noopener noreferrer"
                    style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    Instagram <Icon name="ExternalLink" size={13} />
                  </a>
                  {type === 'noprice' && (
                    <button onClick={() => { setShowApplyModal(true); setApplySuccess(false); setApplyError(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--sedra-tan)', fontSize: 14, cursor: 'pointer', padding: 0, textAlign: lang === 'ar' ? 'right' : 'left', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {lang === 'ar' ? 'تقدّم للحصول على وصول الجملة' : 'Apply for Wholesale Access'} <Icon name="ExternalLink" size={13} />
                    </button>
                  )}
                </div>
              </div>

              {/* Join Us column */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: lang === 'ar' ? 'normal' : '0.14em', textTransform: lang === 'ar' ? 'none' : 'uppercase', color: 'var(--sedra-tan)', marginBottom: 22 }}>
                  {lang === 'ar' ? 'انضم إلينا' : 'Join Us'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <a href="/register"
                    style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}><Icon name="KeyRound" size={15} /></span>
                    <span>{lang === 'ar' ? 'التسجيل للحصول على أسعار الجملة' : 'Apply for wholesale pricing'}</span>
                  </a>
                  <a href="/sell"
                    style={{ color: 'var(--sedra-cream)', fontWeight: 700, fontSize: 14, textDecoration: 'none', display: 'inline-flex', alignItems: 'flex-start', gap: 12 }}>
                    <span style={{ flexShrink: 0, marginTop: 1, display: 'flex' }}><Icon name="Store" size={15} /></span>
                    <span>{lang === 'ar' ? 'كن بائعاً معنا' : 'Become a vendor'}</span>
                  </a>
                </div>
              </div>

            </div>

            {/* Bottom bar */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.10)', paddingTop: 28, display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)' }}>
                {`© ${new Date().getFullYear()} Sedra Home · ${lang === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}`}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.28)' }}>
                {lang === 'ar' ? 'كتالوج الجملة الرسمي' : 'Official Wholesale Catalogue'}
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* ── OTP Verification Modal ── */}
      {otpModal && (
        <div onClick={() => setOtpModal(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.60)', zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:380, boxShadow:'0 20px 60px rgba(0,0,0,0.28)', direction: dir }}>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'center', marginBottom:10 }}><Icon name="Lock" size={36} /></div>
              <h3 style={{ margin:0, fontSize:18, fontWeight:900, color:'var(--sedra-forest)' }}>
                {lang === 'ar' ? 'تحقق من هويتك' : 'Verify Your Identity'}
              </h3>
              <p style={{ margin:'8px 0 0', fontSize:13, color:'#888', lineHeight:1.5 }}>
                {lang === 'ar'
                  ? 'لحماية بيانات طلباتك، نحتاج التحقق من هويتك مرة واحدة في هذه الجلسة.'
                  : 'To protect your order history, we need to verify you once per session.'}
              </p>
            </div>

            {otpError && (
              <div style={{ background:'#fff5f5', border:'1px solid #fecaca', borderRadius:8, padding:'8px 14px', marginBottom:12, fontSize:13, color:'var(--status-danger)', textAlign:'center' }}>
                {otpError}
              </div>
            )}

            {!otpSent ? (
              <>
                <p style={{ fontSize:12, color:'#aaa', textAlign:'center', marginBottom:16 }}>
                  {lang === 'ar' ? 'سنرسل رمز تحقق إلى بريدك أو هاتفك المسجّل.' : 'We will send a 4-digit code to your registered email or phone.'}
                </p>
                <button onClick={sendOtp} disabled={otpSending}
                  style={{ width:'100%', padding:'12px', borderRadius:10, background: otpSending ? '#ccc' : 'var(--sedra-olive)', color:'#fff', border:'none', cursor: otpSending ? 'not-allowed' : 'pointer', fontWeight:800, fontSize:15, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {otpSending ? '…' : (<><Icon name="Send" size={16} /> {lang === 'ar' ? 'أرسل الرمز' : 'Send Code'}</>)}
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize:12, color:'var(--sedra-sage-ink)', textAlign:'center', marginBottom:14 }}>
                  {lang === 'ar' ? 'تم الإرسال إلى' : 'Code sent to'}{' '}
                  {otpMasked.email && <strong>{otpMasked.email}</strong>}
                  {otpMasked.email && otpMasked.phone && ' / '}
                  {otpMasked.phone && <strong>{otpMasked.phone}</strong>}
                </p>
                <input
                  autoFocus
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4}
                  value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g,'').slice(0,4))}
                  onKeyDown={e => e.key === 'Enter' && verifyOtp()}
                  placeholder="• • • •"
                  style={{ width:'100%', textAlign:'center', fontSize:32, fontWeight:900, letterSpacing:14, padding:'14px', borderRadius:10, border:'2px solid var(--sedra-line)', boxSizing:'border-box', outline:'none', fontFamily:'monospace', marginBottom:12 }}
                />
                <button onClick={verifyOtp} disabled={otpVerifying || otpCode.length !== 4}
                  style={{ width:'100%', padding:'12px', borderRadius:10, background: otpCode.length === 4 && !otpVerifying ? 'var(--sedra-olive)' : '#ccc', color:'#fff', border:'none', cursor: otpCode.length === 4 ? 'pointer' : 'not-allowed', fontWeight:800, fontSize:15, marginBottom:10, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                  {otpVerifying ? '…' : (<><Icon name="CheckCircle2" size={16} /> {lang === 'ar' ? 'تحقق' : 'Verify'}</>)}
                </button>
                <button onClick={sendOtp} disabled={otpSending}
                  style={{ width:'100%', padding:'9px', borderRadius:8, background:'none', border:'1.5px solid var(--sedra-line)', color:'var(--sedra-sage-ink)', cursor:'pointer', fontWeight:600, fontSize:13, display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  {otpSending ? '…' : (<><Icon name="Undo2" size={14} /> {lang === 'ar' ? 'أعد الإرسال' : 'Resend Code'}</>)}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Quick Add FAB — token holders only ── */}
      {view === 'catalogue' && _routeToken && (
        <button
          onClick={() => { setShowQuickAdd(true); setQuickAddText(''); setQuickAddResults(null); }}
          title={lang === 'ar' ? 'إضافة سريعة بالكود' : 'Quick Add by SKU'}
          style={{ position:'fixed', bottom: 96, right: lang === 'ar' ? 'auto' : 24, left: lang === 'ar' ? 24 : 'auto', zIndex: 95, width: 50, height: 50, borderRadius: '50%', background: 'var(--sedra-olive)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 18px rgba(74,82,64,0.42)', border: 'none', transition: 'transform 0.2s', color: '#fff' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <Icon name="Zap" size={22} />
        </button>
      )}

      {/* ── Quick Add Modal ── */}
      {showQuickAdd && (
        <div onClick={() => setShowQuickAdd(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:24, width:'100%', maxWidth:500, maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.25)', direction: dir }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:17, color:'var(--sedra-forest)', display:'flex', alignItems:'center', gap:8 }}><Icon name="Zap" size={18} /> {lang === 'ar' ? 'إضافة سريعة' : 'Quick Add'}</h3>
              <button onClick={() => setShowQuickAdd(false)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--sedra-ink-muted)', display:'flex' }}><Icon name="X" size={20} /></button>
            </div>
            <p style={{ margin:'0 0 12px', fontSize:13, color:'#888', lineHeight: 1.6 }}>
              {lang === 'ar'
                ? 'الصق قائمة المنتجات — كل منتج في سطر:'
                : 'Paste a product list — one per line:'}
              <br/>
              <code style={{ fontSize:11, background:'#f4f2ee', padding:'2px 6px', borderRadius:4 }}>
                {lang === 'ar' ? 'SKU001 x 6' : 'SKU001 x 6'}
              </code>
              {'  '}
              <code style={{ fontSize:11, background:'#f4f2ee', padding:'2px 6px', borderRadius:4 }}>SKU002</code>
            </p>
            <textarea
              autoFocus
              value={quickAddText}
              onChange={e => { setQuickAddText(e.target.value); setQuickAddResults(null); }}
              placeholder={lang === 'ar' ? 'SKU001 x 12\nSKU002 x 6\nSKU003' : 'SKU001 x 12\nSKU002 x 6\nSKU003'}
              style={{ width:'100%', height:120, padding:'10px 12px', borderRadius:8, border:'1.5px solid var(--sedra-line)', fontSize:13, fontFamily:'monospace', boxSizing:'border-box', resize:'vertical', outline:'none' }}
            />
            <button onClick={() => setQuickAddResults(parseQuickAdd(quickAddText))}
              disabled={!quickAddText.trim()}
              style={{ marginTop:10, padding:'9px 20px', borderRadius:8, background: quickAddText.trim() ? 'var(--sedra-olive)' : '#ccc', color:'#fff', border:'none', cursor: quickAddText.trim() ? 'pointer' : 'not-allowed', fontWeight:700, fontSize:14, width:'100%', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8 }}>
              <Icon name="Search" size={16} /> {lang === 'ar' ? 'معاينة' : 'Preview'}
            </button>

            {/* Results */}
            {quickAddResults && (
              <div style={{ marginTop:14 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead><tr style={{ background:'var(--sedra-sage-wash)' }}>
                    <th style={{ padding:'6px 10px', textAlign:'left', color:'#666', fontWeight:700 }}>SKU</th>
                    <th style={{ padding:'6px 10px', textAlign:'left', color:'#666', fontWeight:700 }}>{lang === 'ar' ? 'المنتج' : 'Product'}</th>
                    <th style={{ padding:'6px 10px', textAlign:'center', color:'#666', fontWeight:700 }}>{lang === 'ar' ? 'الكمية' : 'Qty'}</th>
                    <th style={{ padding:'6px 10px', textAlign:'center', color:'#666', fontWeight:700 }}>{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                  </tr></thead>
                  <tbody>
                    {quickAddResults.map((r, i) => (
                      <tr key={i} style={{ borderTop:'1px solid #f0ebe3', background: r.status === 'notfound' || r.status === 'invalid' ? '#fff5f5' : '#fff' }}>
                        <td style={{ padding:'6px 10px', fontFamily:'monospace', color:'var(--sedra-forest-soft)', fontWeight:600 }}>{r.sku || r.line}</td>
                        <td style={{ padding:'6px 10px', color:'var(--sedra-forest)' }}>{r.product ? (lang === 'ar' && r.product.name_ar ? r.product.name_ar : r.product.name_en) : '—'}</td>
                        <td style={{ padding:'6px 10px', textAlign:'center', fontWeight:700 }}>{r.qty ?? '—'}</td>
                        <td style={{ padding:'6px 10px', textAlign:'center' }}>
                          {r.status === 'ok'       && <Icon name="CheckCircle2" size={16} color="var(--status-success)" />}
                          {r.status === 'adjusted' && <span style={{ color:'var(--status-warn)', fontWeight:700, display:'inline-flex', alignItems:'center', gap:4 }} title={`Min qty: ${r.product?.min_qty}`}><Icon name="TriangleAlert" size={14} /> {r.qty}</span>}
                          {r.status === 'notfound' && <Icon name="XCircle" size={16} color="var(--status-danger)" />}
                          {r.status === 'invalid'  && <span style={{ color:'#aaa' }}>?</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {quickAddResults.some(r => r.status === 'ok' || r.status === 'adjusted') && (
                  <button onClick={() => applyQuickAdd(quickAddResults)}
                    style={{ marginTop:12, padding:'10px 0', borderRadius:100, background:'var(--sedra-olive)', color:'#fff', border:'none', cursor:'pointer', fontWeight:700, fontSize:14, width:'100%' }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}><ShoppingCart size={15} aria-hidden="true" /> {lang === 'ar'
                      ? `أضف ${quickAddResults.filter(r=>r.status==='ok'||r.status==='adjusted').length} منتج للسلة`
                      : `Add ${quickAddResults.filter(r=>r.status==='ok'||r.status==='adjusted').length} products to cart`}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── WhatsApp FAB — always visible on catalogue ── */}
      {view === 'catalogue' && (
        <div className="sedra-wa-bar"
          style={lang === 'ar' ? { right: 'auto', left: 24 } : undefined}
          title={lang === 'ar' ? 'تواصل معنا على واتساب' : 'Chat with us on WhatsApp'}
          onClick={() => window.open('https://wa.me/201222216247', '_blank')}>
          <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
        </div>
      )}

      {/* ── PRODUCT DETAIL MODAL ── */}
      {selectedProduct && (() => {
        const p = selectedProduct;
        const imgs = Array.isArray(p.images) && p.images.length > 0
              ? p.images
              : (p.image_url || p.image ? [p.image_url || p.image] : []);
        const hasMultiple = imgs.length > 1;
        return (
          <div onClick={() => { setSelectedProduct(null); setDetailLoading(false); setSelectedVariant(null); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: isMobile ? '20px 20px 0 0' : 16, width: isMobile ? '100%' : 820, maxWidth: '95vw', maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', position: 'relative' }}>

              {/* Close button */}
              <button onClick={() => { setSelectedProduct(null); setDetailLoading(false); setSelectedVariant(null); }}
                style={{ position: 'absolute', top: 14, right: 14, zIndex: 10, background: 'rgba(0,0,0,0.22)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}><Icon name="X" size={16} /></button>
              {/* Share button — copies deep-link to clipboard */}
              <button onClick={() => {
                try {
                  const params = new URLSearchParams(window.location.search);
                  params.set('product', p.sku);
                  const shareUrl = window.location.origin + window.location.pathname + '?' + params.toString();
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    setToast(lang === 'ar' ? 'تم نسخ رابط المنتج' : 'Product link copied');
                    setTimeout(() => setToast(''), 2400);
                  });
                } catch {}
              }}
                title={lang === 'ar' ? 'مشاركة المنتج' : 'Share product'}
                style={{ position: 'absolute', top: 14, right: 56, zIndex: 10, background: 'rgba(0,0,0,0.22)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}><Icon name="Link" size={15} /></button>

              {/* Image gallery */}
              <div style={{ width: isMobile ? '100%' : 400, flexShrink: 0, background: 'var(--sedra-image-well)', borderRadius: isMobile ? '20px 20px 0 0' : '16px 0 0 16px', overflow: 'hidden', position: 'relative' }}>
                <div style={{ height: isMobile ? 290 : 440, position: 'relative', overflow: 'hidden' }}>
                  {detailLoading ? (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14 }}>
                      <div style={{ width: 38, height: 38, borderRadius: '50%', border: '3px solid var(--sedra-line)', borderTopColor: 'var(--sedra-forest-soft)', animation: 'spin 0.8s linear infinite' }} />
                    </div>
                  ) : imgs.length > 0
                    ? <img src={imgs[imgIndex]} alt={p.name_en} loading="lazy" onError={e => { e.target.onerror = null; e.target.src = IMG_PH; }} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sedra-line)' }}><HomeIcon size={60} strokeWidth={1.5} /></div>}
                  {hasMultiple && (
                    <>
                      <button onClick={() => setImgIndex(i => (i - 1 + imgs.length) % imgs.length)}
                        style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>‹</button>
                      <button onClick={() => setImgIndex(i => (i + 1) % imgs.length)}
                        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>›</button>
                    </>
                  )}
                </div>
                {/* Thumbnail strip */}
                {hasMultiple && (
                  <div style={{ display: 'flex', gap: 6, padding: '10px 12px', overflowX: 'auto', background: 'var(--sedra-cream)' }}>
                    {imgs.map((img, i) => (
                      <div key={i} onClick={() => setImgIndex(i)}
                        style={{ width: 52, height: 52, flexShrink: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: i === imgIndex ? '2.5px solid var(--sedra-forest-soft)' : '2px solid transparent', opacity: i === imgIndex ? 1 : 0.65 }}>
                        <img src={img} alt="" onError={e => { e.target.onerror = null; e.target.src = IMG_PH; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Product info */}
              <div style={{ flex: 1, padding: isMobile ? '20px 18px 28px' : '32px 28px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>
                {p.category && (
                  <div style={{ fontSize: 10, color: 'var(--sedra-sage-ink)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, display: 'flex', alignItems: 'center', gap: 4 }}><CategoryIcon category={p.category} size={11} /> {lang === 'ar' ? (p.category_ar || catLabel(p.category, 'ar')) : catLabel(p.category, 'en')}</div>
                )}
                <h2 style={{ margin: 0, fontSize: isMobile ? 21 : 25, color: 'var(--sedra-forest)', lineHeight: 1.3, fontWeight: lang === 'ar' ? 800 : 700, fontFamily: lang === 'ar' ? "'Almarai', 'Tajawal', sans-serif" : undefined }}>
                  {lang === 'ar' && p.name_ar ? p.name_ar : p.name_en}
                </h2>
                {lang === 'en' && p.name_ar && (
                  <div style={{ fontSize: 14, color: '#999', direction: 'rtl', textAlign: dir === 'rtl' ? 'left' : 'right', fontFamily: "'Almarai', 'Tajawal', sans-serif", fontWeight: 600 }}>{p.name_ar}</div>
                )}
                <div style={{ fontSize: 12, color: '#bbb', letterSpacing: 0.3 }}>SKU: {p.sku}</div>
                {/* Marketplace: vendor branding in product detail */}
                {(p.vendor_name_en || p.vendor_name_ar) && (
                  <div style={{ fontSize: 12, color: 'var(--sedra-sage-ink)', fontWeight: 700, background: '#f5f7f2', borderRadius: 8, padding: '6px 12px', alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Store size={13} aria-hidden="true" /> {lang === 'ar'
                      ? `يُباع بواسطة ${p.vendor_name_ar || p.vendor_name_en} عبر سدرة هوم`
                      : `Sold by ${p.vendor_name_en} via Sedra Home`}
                  </div>
                )}
                {showPrice && (
                  <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 900, color: 'var(--sedra-forest-soft)', lineHeight: 1, marginTop: 4 }}>
                    {showVat ? fmtVat(p.price) : fmt(p.price)}
                    {showVat && <span style={{ fontSize: 12, color: '#aaa', fontWeight: 400, marginLeft: 8 }}>incl. 14% VAT</span>}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                  {p.min_qty > 1 && (
                    <div style={{ fontSize: 12, color: 'var(--sedra-sage-ink)', background: '#F0EDE7', borderRadius: 8, padding: '6px 12px', display: 'inline-block', fontWeight: 600 }}>
                      {lang === 'ar' ? `الحد الأدنى للطلب: ${p.min_qty} وحدة` : `Min. order: ${p.min_qty} units`}
                    </div>
                  )}
                  {p.carton_qty > 1 && (
                    <div style={{ fontSize: 12, color: '#5a6e8a', background: '#EAF0F8', borderRadius: 8, padding: '6px 12px', display: 'inline-block', fontWeight: 600 }}>
                      {lang === 'ar' ? `الكرتونة: ${p.carton_qty} قطعة` : `${p.carton_qty} pcs / carton`}
                    </div>
                  )}
                </div>
                {(lang === 'ar' && p.description_ar ? p.description_ar : p.description) && (() => {
                  const raw = (lang === 'ar' && p.description_ar ? p.description_ar : p.description) || '';
                  const text = raw.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g,' ').replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&#?\w+;/g,' ').replace(/\s+/g,' ').trim();
                  const truncated = text.length > 420 ? text.slice(0, 420) + '…' : text;
                  return (
                    <div style={{ fontSize: 13, color: '#555', lineHeight: 1.75, borderTop: '1px solid #f0ebe3', paddingTop: 12, marginTop: 4, fontFamily: lang === 'ar' ? "'Tajawal', sans-serif" : undefined, direction: lang === 'ar' ? 'rtl' : 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>
                      {truncated}
                    </div>
                  );
                })()}
                {/* Phase 8: Variant picker — only shown when product has variants */}
                {p._fullLoaded && Array.isArray(p.variants) && p.variants.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6, fontWeight: 600 }}>
                      {lang === 'ar' ? 'اختر الاختيار:' : 'Select Variant:'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, direction: dir }}>
                      {p.variants.map(v => {
                        const isChosen = selectedVariant?.id === v.id;
                        const effectivePrice = (showPrice && v.price_override != null) ? v.price_override : (showPrice ? p.price : null);
                        return (
                          <button key={v.id} type="button"
                            onClick={() => setSelectedVariant(isChosen ? null : v)}
                            style={{
                              padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                              border: '2px solid', fontSize: 13, fontWeight: 600,
                              borderColor: isChosen ? 'var(--sedra-forest-soft)' : 'var(--sedra-line)',
                              background: isChosen ? 'var(--sedra-olive)' : '#fff',
                              color: isChosen ? '#fff' : '#555',
                              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                            }}>
                            <span>{v.label}</span>
                            {showPrice && effectivePrice != null && (
                              <span style={{ fontSize: 11, fontWeight: 700, color: isChosen ? 'var(--sedra-btn-label)' : 'var(--sedra-sage-ink)' }}>
                                {showVat ? fmtVat(effectivePrice) : fmt(effectivePrice)}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Show has_variants hint on catalogue card before detail loads */}
                {!p._fullLoaded && p.has_variants && (
                  <div style={{ fontSize: 12, color: 'var(--sedra-sage-ink)', marginTop: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Sparkles size={12} /> {lang === 'ar' ? 'متوفر بعدة اختيارات — انتظر التحميل…' : 'Available in variants — loading…'}
                  </div>
                )}

                <div style={{display:'flex',alignItems:'center',gap:8,marginTop:12,marginBottom:4}}>
                  <button onClick={()=>setModalQty(q=>Math.max(p.min_qty||1,q-(p.min_qty||1)))} style={{width:32,height:32,borderRadius:'50%',border:'1px solid var(--sedra-line)',background:'#fff',cursor:'pointer',fontSize:18,fontWeight:700,lineHeight:1}}>-</button>
                  <input type="number" className="sdra-qtyInput" value={modalQty} min={p.min_qty||1} step={p.min_qty||1} onChange={e=>setModalQty(Math.max(p.min_qty||1,parseInt(e.target.value)||(p.min_qty||1)))} style={{width:64,textAlign:'center',fontWeight:700,border:'1px solid var(--sedra-line)',borderRadius:8,padding:'5px 6px',fontSize:16,background:'#fff'}} />
                  <button onClick={()=>setModalQty(q=>q+(p.min_qty||1))} style={{width:32,height:32,borderRadius:'50%',border:'1px solid var(--sedra-line)',background:'#fff',cursor:'pointer',fontSize:18,fontWeight:700,lineHeight:1}}>+</button>
                </div>
                {/* Warn if product has variants but none selected */}
                {p._fullLoaded && Array.isArray(p.variants) && p.variants.length > 0 && !selectedVariant && (
                  <div style={{ fontSize: 12, color: 'var(--status-warn)', background: '#fffbeb', borderRadius: 7, padding: '6px 10px', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="TriangleAlert" size={14} /> {lang === 'ar' ? 'الرجاء اختيار اختيار قبل الإضافة للسلة.' : 'Please select a variant before adding to cart.'}
                  </div>
                )}
                <button className="sdra-addBtn"
                  onClick={()=>{
                    const variantsLoaded = p._fullLoaded && Array.isArray(p.variants) && p.variants.length > 0;
                    if (variantsLoaded && !selectedVariant) return;
                    addToCart(p, modalQty, selectedVariant || null);
                    setSelectedProduct(null); setDetailLoading(false); setSelectedVariant(null);
                  }}
                  disabled={p._fullLoaded && Array.isArray(p.variants) && p.variants.length > 0 && !selectedVariant}
                  style={{marginTop:8,padding:isMobile?'13px':'15px',borderRadius:12,background:'var(--sedra-olive)',color:'#fff',border:'none',cursor:'pointer',fontSize:15,fontWeight:800,letterSpacing:0.3,width:'100%',opacity:(p._fullLoaded && Array.isArray(p.variants) && p.variants.length > 0 && !selectedVariant)?0.45:1}}>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:7 }}><ShoppingCart size={16} aria-hidden="true" /> {t.addToCart}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Phase 3: Apply for Wholesale Access Modal ────────────────────── */}
      {showApplyModal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) { setShowApplyModal(false); setApplySuccess(false); } }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(30,40,26,0.72)',
            backdropFilter: 'blur(4px)', zIndex: 2000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: isMobile ? '28px 20px' : '36px 36px',
            width: '100%', maxWidth: 480, boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            fontFamily: lang === 'ar' ? "'Tajawal', sans-serif" : "'Inter', sans-serif",
            direction: lang === 'ar' ? 'rtl' : 'ltr',
            maxHeight: '90vh', overflowY: 'auto',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--sedra-forest)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, fontFamily: lang === 'ar' ? "'Almarai', 'Tajawal', sans-serif" : undefined }}>
                  <Icon name="KeyRound" size={20} /> {lang === 'ar' ? 'طلب وصول الجملة' : 'Apply for Wholesale Access'}
                </div>
                <div style={{ fontSize: 13, color: '#888' }}>
                  {lang === 'ar' ? 'سيراجع فريقنا طلبك خلال 24 ساعة' : 'Our team will review your details within 24 hours'}
                </div>
              </div>
              <button onClick={() => { setShowApplyModal(false); setApplySuccess(false); }}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#aaa', lineHeight: 1, padding: '0 4px' }}>
                ×
              </button>
            </div>

            {applySuccess ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--status-success)' }}><Icon name="CheckCircle2" size={52} /></div>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--sedra-forest)', marginBottom: 8 }}>
                  {lang === 'ar' ? 'تم استلام طلبك!' : 'Application Received!'}
                </div>
                <div style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>
                  {lang === 'ar'
                    ? 'سيقوم فريقنا بمراجعة بياناتك وإرسال رابط الأسعار الخاص بك قريباً.'
                    : 'Application received. Our team will review your details shortly.'}
                </div>
                <button onClick={() => { setShowApplyModal(false); setApplySuccess(false); }}
                  style={{ marginTop: 24, padding: '11px 32px', borderRadius: 8, background: 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
                  {lang === 'ar' ? 'حسناً' : 'Close'}
                </button>
              </div>
            ) : (
              <form onSubmit={submitApplication}>
                {[
                  { key: 'company_name', label: lang === 'ar' ? 'اسم المنشأة / الشركة' : 'Company Name', required: true, type: 'text' },
                  { key: 'contact_name', label: lang === 'ar' ? 'اسم المسؤول' : 'Contact Name', required: true, type: 'text' },
                  { key: 'email',        label: lang === 'ar' ? 'البريد الإلكتروني' : 'Email Address', required: true, type: 'email' },
                  { key: 'phone',        label: lang === 'ar' ? 'رقم الهاتف' : 'Phone Number', required: true, type: 'tel' },
                  { key: 'tax_id',       label: lang === 'ar' ? 'السجل التجاري / الرقم الضريبي (اختياري)' : 'Tax ID / Trade License (optional)', required: false, type: 'text' },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {f.label}{f.required && <span style={{ color: '#e53e3e' }}> *</span>}
                    </label>
                    <input
                      type={f.type}
                      required={f.required}
                      value={applyForm[f.key]}
                      onChange={e => setApplyForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      disabled={applySubmitting}
                      style={{
                        width: '100%', padding: '10px 13px', borderRadius: 8,
                        border: '1.5px solid var(--sedra-line)', fontSize: 14,
                        boxSizing: 'border-box', outline: 'none',
                        background: applySubmitting ? '#f8f8f6' : '#fff',
                        fontFamily: 'inherit', direction: 'ltr',
                      }}
                    />
                  </div>
                ))}
                {applyError && (
                  <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 7, padding: '9px 13px', marginBottom: 14, fontSize: 13, color: '#c53030' }}>
                    {applyError}
                  </div>
                )}
                <button type="submit" disabled={applySubmitting}
                  style={{
                    width: '100%', padding: '13px', borderRadius: 8,
                    background: applySubmitting ? '#aaa' : 'var(--sedra-olive)',
                    color: '#fff', border: 'none', cursor: applySubmitting ? 'not-allowed' : 'pointer',
                    fontSize: 15, fontWeight: 700, letterSpacing: 0.3,
                    transition: 'background 0.2s',
                  }}>
                  {applySubmitting
                    ? (lang === 'ar' ? 'جاري الإرسال…' : 'Submitting…')
                    : (lang === 'ar' ? 'إرسال الطلب' : 'Submit Application')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── MAGIC LINK RECOVERY PAGE ─────────────────────────────────────────────────
function MagicLinkLogin() {
  const [lang]      = useState(() => localStorage.getItem('sedra_lang') || 'ar');
  const ar          = lang === 'ar';
  const dir         = ar ? 'rtl' : 'ltr';
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [done, setDone]         = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await fetch(`${SUPABASE_URL}/functions/v1/recover-access-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'apikey': SUPABASE_KEY },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      setDone(true);
    } catch {
      setError(ar ? 'حدث خطأ. حاول مرة أخرى.' : 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(155deg,#242c21 0%,#3A4534 38%,#4c5845 68%,#5e6c50 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: ar ? "'Tajawal',sans-serif" : "'DM Sans',sans-serif", direction: dir }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '40px 36px', width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src={LOGO_URL} alt="Sedra Home" style={{ height: 52, marginBottom: 16 }} />
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 900, color: 'var(--sedra-forest)' }}>
            {ar ? 'استرداد رابط الوصول' : 'Recover Your Access Link'}
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#888', lineHeight: 1.6 }}>
            {ar
              ? 'أدخل بريدك الإلكتروني وسنرسل لك رابطك الخاص إن كان حسابك مفعّلاً.'
              : 'Enter your email and we\'ll send your personal catalogue link if your account is approved.'}
          </p>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'var(--status-success)' }}><Icon name="Send" size={44} /></div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--status-success)' }}>
              {ar ? 'تم الإرسال!' : 'Sent!'}
            </h3>
            <p style={{ fontSize: 13, color: '#666', marginTop: 8, lineHeight: 1.7 }}>
              {ar
                ? 'إذا كان حسابك مفعّلاً، ستصل رسالة برابط وصولك على بريدك الإلكتروني.'
                : 'If your account is approved, your access link has been sent to your email.'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ background: 'var(--status-danger-bg)', border: '1px solid var(--status-danger-bg)', borderRadius: 8, padding: '9px 14px', marginBottom: 14, fontSize: 13, color: 'var(--status-danger)' }}>
                {error}
              </div>
            )}
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 6, fontWeight: 600 }}>
              {ar ? 'البريد الإلكتروني' : 'Email Address'}
            </label>
            <input
              type="email" required autoFocus
              value={email} onChange={e => setEmail(e.target.value)}
              placeholder={ar ? 'example@email.com' : 'your@email.com'}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid var(--sedra-line)', fontSize: 14, boxSizing: 'border-box', outline: 'none', marginBottom: 16, direction: 'ltr' }}
            />
            <button type="submit" disabled={loading || !email.trim()}
              style={{ width: '100%', padding: '13px', borderRadius: 100, background: loading || !email.trim() ? '#ccc' : 'var(--sedra-olive)', color: '#fff', border: 'none', cursor: loading || !email.trim() ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {!loading && <Icon name="Send" size={15} />} {loading ? '…' : (ar ? 'أرسل رابطي' : 'Send My Link')}
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: '#bbb' }}>
          {ar ? 'ليس لديك حساب؟' : "Don't have an account?"}{' '}
          <a href="/" style={{ color: 'var(--sedra-sage-ink)', fontWeight: 700, textDecoration: 'none' }}>
            {ar ? 'تصفح الكتالوج' : 'Browse catalog'}
          </a>
        </p>
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<CatalogueView />} />
          <Route path="/catalogue/:token" element={<CatalogueView />} />
          <Route path="/login" element={<MagicLinkLogin />} />
          <Route path="/sell" element={<VendorApply />} />
          <Route path="/register" element={<RetailerRegister />} />
          <Route path="/admin" element={<AdminShell />} />
          <Route path="/admin/*" element={<AdminShell />} />
          <Route path="*" element={<CatalogueView />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
